import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBook } from '../contexts/BookContext';
import { useAuth } from '../contexts/AuthContext';
import { generateImage } from '../services/bookService/operations/generateImage';
import { generateCharacterImageByJSON } from '../services/bookService/operations/generateCharacterImageByJSON';
import { generatePlaceImageByJSON } from '../services/bookService/operations/generatePlaceImageByJSON';
import { generateThingImageByJSON } from '../services/bookService/operations/generateThingImageByJSON';
import { generateMainCharacter } from '../services/bookService/operations/generateMainCharacter';
import { ensureDesignPrompts } from '../services/bookService/operations/ensureDesignPrompts';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { db } from '../config/firebase';
import { doc, onSnapshot, getDoc, getDocFromServer } from 'firebase/firestore';
import { useWorldObjectJobsByBook, useWorldObjectPendingJobs } from '../hooks/useWorldObjectJobs';
import { visualStyles } from '../data/styleOptions';
import { useCredits } from '../contexts/CreditsContext';
import { notify, toastIds } from '../utils/notify.jsx';
import { WorldObjectModal } from '../components/WorldPage/WorldObjectModal';
import TutorialModal from '../components/TutorialSlideshow/TutorialModal';
import { DESIGN_STATUS_MESSAGES } from '../constants/designStatuses';
import BulkImageGenerationOverlay from '../components/BookDesign/overlays/BulkImageGenerationOverlay';
import MinimizedGenerationProgress from '../components/BookDesign/overlays/MinimizedGenerationProgress';

export default function WorldPage() {
  const navigate = useNavigate();
  const { bookId } = useParams();
  const { currentBook, loadBook, loading, error, updateBook, processingStatus, loadingUIMessage } = useBook();
  const { user } = useAuth();

  // Listen to world object generation jobs for this book
  const { jobs: worldObjectJobs } = useWorldObjectJobsByBook(currentBook?.id);
  
  // Listen to PENDING world object image generation jobs for real-time loading UI
  const { pendingJobs } = useWorldObjectPendingJobs(currentBook?.id);

  // Redirect to /edit only if no design exists (no spreads) AND no characterData
  // If spreads exist, stay on /world and show loading UI while characterData is being generated
  useEffect(() => {
    if (!loading && currentBook && !currentBook.characterData && !currentBook.spreads) {
      navigate(`/edit/${bookId}`, { replace: true });
    }
  }, [loading, currentBook, bookId, navigate]);

  const [activeTab, setActiveTab] = useState('important');
  const [showTutorialModal, setShowTutorialModal] = useState(false);
  const [editing, setEditing] = useState({}); // { [uid]: { ...editedCharacter } }
  const [savingIdx, setSavingIdx] = useState(null);
  const [editingPlaces, setEditingPlaces] = useState({}); // { [uid]: { ...editedPlace } }
  const [savingPlaceUid, setSavingPlaceUid] = useState(null);
  const [editingThings, setEditingThings] = useState({}); // { [uid]: { ...editedThing } }
  const [savingThingUid, setSavingThingUid] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [charactersExpanded, setCharactersExpanded] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenStatus, setRegenStatus] = useState(null); // 'pending' | 'completed' | 'error' | null
  const [bulkStateByTab, setBulkStateByTab] = useState({
    important: { generating: false, total: 0, startPendingCount: 0, hasSeenPendingJobs: false, selectedStyle: '', generatingMode: null },
    characters: { generating: false, total: 0, startPendingCount: 0, hasSeenPendingJobs: false, selectedStyle: '', generatingMode: null },
    places: { generating: false, total: 0, startPendingCount: 0, hasSeenPendingJobs: false, selectedStyle: '', generatingMode: null },
    things: { generating: false, total: 0, startPendingCount: 0, hasSeenPendingJobs: false, selectedStyle: '', generatingMode: null },
  });
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [singleDialog, setSingleDialog] = useState({ open: false, type: null, idx: null });
  const [singleSelectedVisualStyle, setSingleSelectedVisualStyle] = useState('');
  // When we regenerate, fetch fresh characterData and use it immediately
  const [charDataOverride, setCharDataOverride] = useState(null);
  const [addCharField, setAddCharField] = useState({}); // { [idx]: key }
  const [addCharValue, setAddCharValue] = useState({}); // { [idx]: value }
  const [addGroupField, setAddGroupField] = useState({}); // { `${idx}::${groupKey}`: key }
  const [addGroupValue, setAddGroupValue] = useState({}); // { `${idx}::${groupKey}`: value }
  const [addPlaceField, setAddPlaceField] = useState({});
  const [addPlaceValue, setAddPlaceValue] = useState({});
  const [addThingField, setAddThingField] = useState({});
  const [addThingValue, setAddThingValue] = useState({});
  const [charRegenStatus, setCharRegenStatus] = useState(null); // 'pending' | 'error' | null
  const [placesRegenStatus, setPlacesRegenStatus] = useState(null); // 'pending' | 'error' | null
  const [thingsRegenStatus, setThingsRegenStatus] = useState(null); // 'pending' | 'error' | null
  const [modalState, setModalState] = useState({ isOpen: false, objectType: null, uid: null });
  const [requestingImageUids, setRequestingImageUids] = useState(new Set()); // Track in-flight generation requests (before job appears in Firestore)
  const { checkCredits } = useCredits();
  const GENERATION_COST = 5;
  
  // Helper functions for tab-specific bulk generation state
  const getBulkState = (tab) => bulkStateByTab[tab] || {};
  const setBulkState = (tab, updates) => {
    setBulkStateByTab(prev => ({
      ...prev,
      [tab]: { ...prev[tab], ...updates }
    }));
  };
  
  // Generate a stable UID for world elements
  const generateElementUid = (element) => {
    if (element._uid) return element._uid;
    const name = typeof element === 'object' && element !== null ? (element.name || element.Name || element.mainName || '') : '';
    return `${name}-${Math.random().toString(36).substr(2, 9)}`;
  };

  const isObjectImageInProcess = (uid) => {
    return requestingImageUids.has(uid) || pendingJobs.has(uid);
  };
  
  // Ensure all world elements have UIDs
  const ensureWorldElementsHaveUids = (world) => {
    if (!world) return world;
    const updated = { ...world };
    if (Array.isArray(updated.characters)) {
      updated.characters = updated.characters.map(c => (c._uid ? c : { ...c, _uid: generateElementUid(c) }));
    }
    if (Array.isArray(updated.places)) {
      updated.places = updated.places.map(p => (p._uid ? p : { ...p, _uid: generateElementUid(p) }));
    }
    if (Array.isArray(updated.things)) {
      updated.things = updated.things.map(t => (t._uid ? t : { ...t, _uid: generateElementUid(t) }));
    }
    return updated;
  };



  // Modal helpers
  const openModal = (objectType, uid) => {
    setModalState({ isOpen: true, objectType, uid });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, objectType: null, uid: null });
  };

  const getModalObject = () => {
    if (!modalState.objectType || !modalState.uid) return null;
    const world = ensureWorldElementsHaveUids(currentBook?.world || {});
    if (modalState.objectType === 'character') {
      return (world.characters || []).find(c => (c._uid || generateElementUid(c)) === modalState.uid);
    } else if (modalState.objectType === 'place') {
      return (world.places || []).find(p => (p._uid || generateElementUid(p)) === modalState.uid);
    } else if (modalState.objectType === 'thing') {
      return (world.things || []).find(t => (t._uid || generateElementUid(t)) === modalState.uid);
    }
    return null;
  };

  const handleModalSave = async (uid, updatedData) => {
    const world = ensureWorldElementsHaveUids(currentBook?.world || {});
    let updated;

    if (modalState.objectType === 'character') {
      updated = (world.characters || []).map(c => (c._uid === uid ? { ...c, ...updatedData } : c));
      await updateBook(currentBook.id, { world: { ...world, characters: updated } });
    } else if (modalState.objectType === 'place') {
      updated = (world.places || []).map(p => (p._uid === uid ? { ...p, ...updatedData } : p));
      await updateBook(currentBook.id, { world: { ...world, places: updated } });
    } else if (modalState.objectType === 'thing') {
      updated = (world.things || []).map(t => (t._uid === uid ? { ...t, ...updatedData } : t));
      await updateBook(currentBook.id, { world: { ...world, things: updated } });
    }
  };

  const handleModalDelete = async (uid) => {
    const world = ensureWorldElementsHaveUids(currentBook?.world || {});
    let updated;

    if (modalState.objectType === 'character') {
      updated = (world.characters || []).filter(c => c._uid !== uid);
      await updateBook(currentBook.id, { world: { ...world, characters: updated } });
      updated = (world.places || []).filter(p => p._uid !== uid);
      await updateBook(currentBook.id, { world: { ...world, places: updated } });
    } else if (modalState.objectType === 'thing') {
      updated = (world.things || []).filter(t => t._uid !== uid);
      await updateBook(currentBook.id, { world: { ...world, things: updated } });
    }
  };

  const handleModalGenerateImage = async (uid, selectedArtStyle) => {
    const object = getModalObject();
    if (!object) return;

    // Guard against double-clicks: block if a request is already in flight or job is pending
    if (requestingImageUids.has(uid) || pendingJobs.has(uid)) return;
    setRequestingImageUids(prev => new Set(prev).add(uid));

    const clearRequesting = () => {
      setRequestingImageUids(prev => {
        const next = new Set(prev);
        next.delete(uid);
        return next;
      });
    };

    const has = await checkCredits(GENERATION_COST);
    if (!has) {
      alert(`Insufficient credits. ${GENERATION_COST} credits required.`);
      clearRequesting();
      return;
    }

    try {
      const { imageUrl: _omitImageUrl, referenceImage: _omitReferenceImage, ...objectNoImage } = object;
      const payload = {
        type: modalState.objectType,
        [modalState.objectType]: objectNoImage,
      };
      const artStyle = selectedArtStyle || currentBook?.visualStyle?.value || currentBook?.visualStyle || 'Digital SciFi Art';
      
      let resp;
      if (modalState.objectType === 'character') {
        resp = await generateCharacterImageByJSON(artStyle, JSON.stringify(payload), currentBook.id, uid);
      } else if (modalState.objectType === 'place') {
        resp = await generatePlaceImageByJSON(artStyle, JSON.stringify(payload), currentBook.id, uid);
      } else if (modalState.objectType === 'thing') {
        resp = await generateThingImageByJSON(artStyle, JSON.stringify(payload), currentBook.id, uid);
      }

      const d = JSON.parse(JSON.stringify(resp || {}));
      
      // Check if we got a jobId (new async generation flow)
      if (d?.jobId) {
        const objectName = getName(object) || 'Object';
        notify.info(`Image generation started for ${objectName}...`, { toastId: `world-image-start-${uid}` });
        // Charged server-side by generate*ImageByJSON (helpers/worldObjectImage.js).
        // Delay clearing so the pendingJobs listener has time to pick up the new job
        setTimeout(clearRequesting, 3000);
        closeModal();
        return;
      }

      // Legacy synchronous flow (if imageUrl is returned directly)
      const resultText = typeof d.result === 'undefined' ? null : d.result;
      let url = null;

      if (typeof resultText === 'string') {
        if (resultText.startsWith('http')) {
          url = resultText.trim();
        } else {
          try {
            const obj = JSON.parse(resultText);
            url = obj?.imageUrl || obj?.url || null;
          } catch (_) {}
          if (!url) {
            const idxHttp = resultText.indexOf('http');
            if (idxHttp >= 0) {
              const end = resultText.indexOf(' ', idxHttp);
              url = end >= 0 ? resultText.slice(idxHttp, end) : resultText.slice(idxHttp);
            }
          }
        }
      }
      if (!url && typeof d.imageUrl === 'string') {
        url = d.imageUrl;
      }
      if (!url) {
        throw new Error('No image URL returned');
      }

      // Validate image by checking if it can be loaded
      await new Promise((resolve, reject) => {
        const img = new Image();
        const timeout = setTimeout(() => {
          reject(new Error('Image load timeout'));
        }, 10000); // 10 second timeout
        
        img.onload = () => {
          clearTimeout(timeout);
          resolve();
        };
        img.onerror = () => {
          clearTimeout(timeout);
          reject(new Error('Image is corrupted or invalid'));
        };
        img.src = url;
      });

      const imageHistory = object.imageHistory || [];
      const newHistory = [
        { url, generatedAt: new Date().toISOString(), artStyle },
        ...imageHistory.slice(0, 9),
      ];

      await handleModalSave(uid, {
        referenceImage: url,
        imageHistory: newHistory,
      });
      // Charged server-side by generate*ImageByJSON (helpers/worldObjectImage.js).
      notify.success('Image generated successfully!', { toastId: 'world-image-generated' });
      clearRequesting();
    } catch (err) {
      console.error('[WorldPage] Modal image generation error', err);
      notify.error('Failed to generate image', { toastId: 'world-image-error' });
      clearRequesting();
    }
  };
  
  // Stabilize view: capture first non-empty world for this session
  const worldStableRef = useRef({ bookId: null, world: null });
  // Prevent stale Firestore 'pending' from re-showing banner right after success
  const ignorePendingUntilRef = useRef(0);
  useEffect(() => {
    if (!currentBook?.id) return;
    if (worldStableRef.current.bookId !== currentBook.id) {
      worldStableRef.current = { bookId: currentBook.id, world: null };
    }
    const w = currentBook?.world || {};
    const hasAny = !!(
      (Array.isArray(w.characters) && w.characters.length) ||
      (Array.isArray(w.places) && w.places.length) ||
      (Array.isArray(w.things) && w.things.length)
    );
    if (!hasAny) {
      worldStableRef.current.world = null;
      return;
    }
    if (hasAny && !worldStableRef.current.world) {
      worldStableRef.current.world = JSON.parse(JSON.stringify(w));
    }
  }, [currentBook?.id, currentBook?.world]);
  const worldForDisplay = worldStableRef.current.world || currentBook?.world || {};
  const hashString = (s) => {
    let h = 0;
    for (let i = 0; i < s.length; i++) {
      h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    }
    return String(h >>> 0);
  };

  const clearPendingUI = () => {
    setRegenStatus(null);
    setRegenerating(false);
    try {
      const key = `charRegen:${currentBook?.id || bookId}`;
      if (typeof window !== 'undefined') window.sessionStorage.removeItem(key);
    } catch (_) {}
  };

  // When regeneration finishes, drop the cached world so UI picks up fresh data
  useEffect(() => {
    if (!currentBook?.id) return;
    if (!regenerating && regenStatus !== 'pending') {
      try { worldStableRef.current.world = null; } catch (_) {}
    }
  }, [currentBook?.id, regenerating, regenStatus]);

  // Set default visual style when opening the bulk modal (per tab)
  useEffect(() => {
    if (!bulkDialogOpen) return;
    try {
      const bookStyle = (currentBook?.visualStyle && (currentBook.visualStyle.value || currentBook.visualStyle)) || '';
      const currentBulkState = getBulkState(activeTab);
      if (!currentBulkState.selectedStyle) {
        setBulkState(activeTab, { selectedStyle: bookStyle || '' });
      }
    } catch (_) {}
  }, [bulkDialogOpen, activeTab, currentBook?.visualStyle]);

  // Set default visual style when opening the single-generate modal
  useEffect(() => {
    if (!singleDialog.open) return;
    try {
      const bookStyle = (currentBook?.visualStyle && (currentBook.visualStyle.value || currentBook.visualStyle)) || '';
      setSingleSelectedVisualStyle((prev) => (prev || bookStyle || ''));
    } catch (_) {}
  }, [singleDialog.open, currentBook?.visualStyle]);

  // Check if image generation is still in progress
  // Show loading UI for all statuses except completed and error
  const isProcessing = processingStatus && processingStatus !== 'completed' && processingStatus !== 'error';

  // Derive progress from the status mapping (same source as the minimized loading
  // overlay in WizardLayout) so both progress bars always show the same value
  const designProgress = DESIGN_STATUS_MESSAGES[processingStatus]?.progress ?? loadingUIMessage?.progress ?? 0;

  // Real-time listener for character regeneration status
  useEffect(() => {
    if (!currentBook?.id) return;
    
    const statusRef = doc(db, 'characterRegeneration', currentBook.id);
    const unsubscribe = onSnapshot(statusRef, (snap) => {
      if (!snap.exists()) {
        console.log('[WorldPage] Character regeneration status doc deleted, reloading book data');
        setCharRegenStatus(null);
        // Reload book data when regeneration completes
        loadBook(currentBook.id).catch(err => console.error('Failed to reload book:', err));
        return;
      }
      
      const data = snap.data();
      const status = data?.status;
      console.log('[WorldPage] Character regeneration status:', status);
      
      if (status === 'pending') {
        setCharRegenStatus('pending');
      } else if (status === 'error') {
        setCharRegenStatus('error');
      } else {
        setCharRegenStatus(null);
      }
    }, (error) => {
      // Handle permission errors gracefully - document may not exist yet
      if (error.code === 'permission-denied') {
        console.log('[WorldPage] Permission denied reading characterRegeneration - document may not exist yet');
        setCharRegenStatus(null);
      } else {
        console.error('[WorldPage] Error listening to characterRegeneration:', error);
      }
    });
    
    return unsubscribe;
  }, [currentBook?.id, loadBook]);

  // Real-time listener for places regeneration status
  useEffect(() => {
    if (!currentBook?.id) return;
    
    const statusRef = doc(db, 'placesRegeneration', currentBook.id);
    const unsubscribe = onSnapshot(statusRef, (snap) => {
      if (!snap.exists()) {
        console.log('[WorldPage] Places regeneration status doc deleted, reloading book data');
        setPlacesRegenStatus(null);
        // Reload book data when regeneration completes
        loadBook(currentBook.id).catch(err => console.error('Failed to reload book:', err));
        return;
      }
      
      const data = snap.data();
      const status = data?.status;
      console.log('[WorldPage] Places regeneration status:', status);
      
      if (status === 'pending') {
        setPlacesRegenStatus('pending');
      } else if (status === 'error') {
        setPlacesRegenStatus('error');
      } else {
        setPlacesRegenStatus(null);
      }
    }, (error) => {
      // Handle permission errors gracefully - document may not exist yet
      if (error.code === 'permission-denied') {
        console.log('[WorldPage] Permission denied reading placesRegeneration - document may not exist yet');
        setPlacesRegenStatus(null);
      } else {
        console.error('[WorldPage] Error listening to placesRegeneration:', error);
      }
    });
    
    return unsubscribe;
  }, [currentBook?.id, loadBook]);

  // Real-time listener for things regeneration status
  useEffect(() => {
    if (!currentBook?.id) return;
    
    const statusRef = doc(db, 'thingsRegeneration', currentBook.id);
    const unsubscribe = onSnapshot(statusRef, (snap) => {
      if (!snap.exists()) {
        console.log('[WorldPage] Things regeneration status doc deleted, reloading book data');
        setThingsRegenStatus(null);
        // Reload book data when regeneration completes
        loadBook(currentBook.id).catch(err => console.error('Failed to reload book:', err));
        return;
      }
      
      const data = snap.data();
      const status = data?.status;
      console.log('[WorldPage] Things regeneration status:', status);
      
      if (status === 'pending') {
        setThingsRegenStatus('pending');
      } else if (status === 'error') {
        setThingsRegenStatus('error');
      } else {
        setThingsRegenStatus(null);
      }
    }, (error) => {
      // Handle permission errors gracefully - document may not exist yet
      if (error.code === 'permission-denied') {
        console.log('[WorldPage] Permission denied reading thingsRegeneration - document may not exist yet');
        setThingsRegenStatus(null);
      } else {
        console.error('[WorldPage] Error listening to thingsRegeneration:', error);
      }
    });
    
    return unsubscribe;
  }, [currentBook?.id, loadBook]);

  const handleGenerateAllImages = async (mode = 'missing', styleOverride) => {
    if (!currentBook?.id) return;
    const chosenStyle = styleOverride || (currentBook?.visualStyle?.value || currentBook?.visualStyle || 'Digital SciFi Art');
    
    try {
      setBulkState(activeTab, { generating: true, generatingMode: mode });
      let list = [];
      let runOne = null;
      let objectsToGenerate = [];

      const shouldIncludeForMode = (obj) => {
        const matchesMode = mode === 'missing' ? !obj?.referenceImage : !!obj?.referenceImage;
        return matchesMode && !isObjectImageInProcess(obj?._uid);
      };

      // Determine which objects to generate based on active tab
      if (activeTab === 'important') {
        const arr = importantObjects || [];
        list = arr.map((obj, i) => (shouldIncludeForMode(obj) ? i : null)).filter((i) => i !== null);
        objectsToGenerate = list.map(idx => {
          const obj = importantObjects[idx] || {};
          return {
            idx,
            uid: obj._uid || generateElementUid(obj),
            objectType: obj._objectType,
            object: obj,
            displayName: getName(obj),
          };
        });
        runOne = async (objData) => {
          const { imageUrl: _omitImageUrl, referenceImage: _omitReferenceImage, _objectType: _omitType, ...objNoImage } = objData.object;
          const payload = { type: objData.objectType, [objData.objectType]: objNoImage };
          let resp = {};
          if (objData.objectType === 'character') {
            resp = await generateCharacterImageByJSON(chosenStyle, JSON.stringify(payload), currentBook.id, objData.uid);
          } else if (objData.objectType === 'place') {
            resp = await generatePlaceImageByJSON(chosenStyle, JSON.stringify(payload), currentBook.id, objData.uid);
          } else if (objData.objectType === 'thing') {
            resp = await generateThingImageByJSON(chosenStyle, JSON.stringify(payload), currentBook.id, objData.uid);
          }
          return { ...objData, jobId: (resp && typeof resp === 'object' && 'jobId' in resp) ? resp.jobId : null };
        };
      } else if (activeTab === 'characters') {
        const arr = Array.isArray(displayCharacters) ? displayCharacters : [];
        list = arr.map((c, i) => (shouldIncludeForMode(c) ? i : null)).filter((i) => i !== null);
        objectsToGenerate = list.map(idx => {
          const char = displayCharacters[idx] || {};
          return {
            idx,
            uid: char._uid || generateElementUid(char),
            objectType: 'character',
            object: char,
            displayName: getName(char),
          };
        });
        runOne = async (objData) => {
          const { imageUrl: _omitImageUrl, referenceImage: _omitReferenceImage, ...characterNoImage } = objData.object;
          const payload = { type: 'character', character: characterNoImage };
          const bookIdToPass = currentBook?.id;
          const objectIdToPass = objData?.uid;
          console.log('[WorldPage] About to call generateCharacterImageByJSON:', { 
            bookIdToPass, 
            objectIdToPass,
            currentBookKeys: Object.keys(currentBook || {}),
            objDataKeys: Object.keys(objData || {})
          });
          const resp = await generateCharacterImageByJSON(chosenStyle, JSON.stringify(payload), bookIdToPass, objectIdToPass);
          // Response will be { jobId, status: 'pending' } - webhook will update it later
          if (resp && typeof resp === 'object' && 'jobId' in resp) {
            return { ...objData, jobId: resp.jobId };
          }
          throw new Error('No jobId returned from generation');
        };
      } else if (activeTab === 'places') {
        const arr = Array.isArray(displayPlaces) ? displayPlaces : [];
        list = arr.map((p, i) => (shouldIncludeForMode(p) ? i : null)).filter((i) => i !== null);
        objectsToGenerate = list.map(idx => {
          const place = displayPlaces[idx] || {};
          return {
            idx,
            uid: place._uid || generateElementUid(place),
            objectType: 'place',
            object: place,
            displayName: getName(place),
          };
        });
        runOne = async (objData) => {
          const { imageUrl: _omitImageUrl, referenceImage: _omitReferenceImage, ...placeNoImage } = objData.object;
          const payload = { type: 'place', place: placeNoImage };
          const resp = await generatePlaceImageByJSON(chosenStyle, JSON.stringify(payload), currentBook.id, objData.uid);
          // Response will be { jobId, status: 'pending' } - webhook will update it later
          if (resp && typeof resp === 'object' && 'jobId' in resp) {
            return { ...objData, jobId: resp.jobId };
          }
          throw new Error('No jobId returned from generation');
        };
      } else if (activeTab === 'things') {
        const arr = Array.isArray(displayThings) ? displayThings : [];
        list = arr.map((t, i) => (shouldIncludeForMode(t) ? i : null)).filter((i) => i !== null);
        objectsToGenerate = list.map(idx => {
          const thing = displayThings[idx] || {};
          return {
            idx,
            uid: thing._uid || generateElementUid(thing),
            objectType: 'thing',
            object: thing,
            displayName: getName(thing),
          };
        });
        runOne = async (objData) => {
          const { imageUrl: _omitImageUrl, referenceImage: _omitReferenceImage, ...thingNoImage } = objData.object;
          const payload = { type: 'thing', thing: thingNoImage };
          const resp = await generateThingImageByJSON(chosenStyle, JSON.stringify(payload), currentBook.id, objData.uid);
          // Response will be { jobId, status: 'pending' } - webhook will update it later
          if (resp && typeof resp === 'object' && 'jobId' in resp) {
            return { ...objData, jobId: resp.jobId };
          }
          throw new Error('No jobId returned from generation');
        };
      }

      setBulkState(activeTab, { total: list.length, startPendingCount: pendingJobsCount });
      
      const totalRequested = list.length;
      if (totalRequested === 0) {
        return;
      }

      // Check credits (pre-flight check only, not deduction)
      const hasCredits = await checkCredits(totalRequested * GENERATION_COST);
      if (!hasCredits) {
        alert(`Insufficient credits. You need ${totalRequested * GENERATION_COST} credits.`);
        return;
      }

      // Create all generation jobs and collect jobIds
      const jobResults = [];
      for (const objData of objectsToGenerate) {
        try {
          const result = await runOne(objData);
          if (result.jobId) {
            jobResults.push(result);
          }
        } catch (err) {
          console.error(`[WorldPage] Failed to create generation job for ${objData.displayName}:`, err);
        }
      }

      if (jobResults.length === 0) {
        notify.error('Failed to create any generation jobs', { toastId: 'world-bulk-image-no-jobs' });
        return;
      }

      // Jobs are now created and Flux is generating in the background
      // The Firestore listener (worldObjectJobs) will automatically track status changes
      // and update the UI when jobs complete
      // No toast shown - the minimized modal will display progress instead
    } catch (err) {
      console.error('[WorldPage] handleGenerateAllImages error:', err);
      notify.error('Error during bulk generation', { toastId: 'world-bulk-image-error' });
      setBulkState(activeTab, { generating: false, generatingMode: null });
    }
    // Don't set generating to false here - let it stay true until all jobs complete
    // The modal will stay minimized and show progress until pendingJobsCount reaches 0
  };

  const formatValue = (v) => {
    if (v == null) return '';
    if (typeof v !== 'object') return String(v);
    if (Array.isArray(v)) return v.map((x) => (typeof x === 'object' ? Object.values(x).filter((y) => typeof y !== 'object').join(' ') : String(x))).filter(Boolean).join(', ');
    if (v.young || v.adult) {
      const parts = [];
      if (v.young?.ageRange) parts.push(v.young.ageRange);
      if (v.adult?.ageRange) parts.push(v.adult.ageRange);
      if (parts.length) return parts.join(' | ');
    }
    if (v.bodyPosture || v.facialExpression || v.handPositioning) {
      return [v.bodyPosture, v.facialExpression, v.handPositioning].filter(Boolean).join(' | ');
    }
    return Object.values(v).filter((x) => x != null && typeof x !== 'object').map(String).join(', ');
  };

  const getName = (o) => {
    if (o == null) return '';
    if (typeof o !== 'object') return String(o).trim();
    const primary = o.name ?? o.Name ?? o.mainName ?? o.nickName ?? o.nickname ?? o.Alias ?? o.alias ?? o.TrueName ?? o.trueName;
    const toText = (v) => Array.isArray(v) ? v.filter(Boolean).map(String).join(', ') : (typeof v === 'object' ? '' : String(v ?? ''));
    if (primary != null && String(primary).trim() !== '') return toText(primary).trim();
    for (const [k, v] of Object.entries(o)) {
      if (/name/i.test(k) && v != null && typeof v !== 'object' && String(v).trim()) return String(v).trim();
    }
    for (const [k, v] of Object.entries(o)) {
      if (/ame/i.test(k) && v != null && typeof v !== 'object' && String(v).trim()) return String(v).trim();
    }
    return '';
  };

  const flattenCharacter = (c) => {
    if (!c || typeof c !== 'object') return c;
    const base = { ...c };
    if (!base.name && base.mainName) {
      base.name = base.mainName;
    }
    try {
      let sourceVar = Array.isArray(c.outfitVariation) && c.outfitVariation[0] ? c.outfitVariation[0] : null;
      // Fallback: some schemas store these fields on progression[0] instead of outfitVariation
      if (!sourceVar && Array.isArray(c.progression) && c.progression[0]) {
        sourceVar = c.progression[0];
      }

      if (sourceVar) {
        // Copy all keys from sourceVar onto the character if not already present,
        // without trying to interpret or rename them, so arbitrary schemas are preserved.
        Object.entries(sourceVar).forEach(([key, value]) => {
          if (base[key] == null) {
            base[key] = value;
          }
        });
      }

      const firstProg = Array.isArray(c.progression) && c.progression[0] ? c.progression[0] : null;
      if (firstProg) {
        if (firstProg.description && base.backstory == null) base.backstory = firstProg.description;
        if (firstProg.emotionalState && base.emotionalState == null) base.emotionalState = firstProg.emotionalState;
      }
    } catch (_) {}
    return base;
  };

  const flattenPlace = (p) => {
    if (!p || typeof p !== 'object') return p;
    const base = { ...p };
    if (!base.name && base.mainName) {
      base.name = base.mainName;
    }
    return base;
  };

  const flattenThing = (t) => {
    if (!t || typeof t !== 'object') return t;
    const base = { ...t };
    if (!base.name && base.mainName) {
      base.name = base.mainName;
    }
    return base;
  };

  useEffect(() => {
    if (bookId && (!currentBook || currentBook.id !== bookId)) {
      loadBook(bookId);
    }
  }, [bookId]);

  // Restore pending state from sessionStorage so UI persists across reloads
  useEffect(() => {
    if (!bookId) return;
    try {
      const key = `charRegen:${bookId}`;
      const val = typeof window !== 'undefined' ? window.sessionStorage.getItem(key) : null;
      if (val === 'pending') {
        setRegenerating(true);
        setRegenStatus('pending');
      }
    } catch (_) {}
  }, [bookId]);

  // Log characterData for debugging when available
  useEffect(() => {
    if (!currentBook) return;
    try {
      // Targeted debug for quick filtering
      console.log('this is it there should be characterData', currentBook?.characterData);
      const raw = currentBook.characterData;
      console.log('[WorldPage] characterData (raw):', raw);
      if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw);
          console.log('[WorldPage] characterData (parsed):', parsed);
        } catch (e) {
          console.warn('[WorldPage] characterData is a string but not valid JSON');
        }
      }
    } catch (e) {
      console.warn('[WorldPage] Failed to read characterData', e);
    }
  }, [currentBook?.id, currentBook?.characterData]);

  // Listen for background character regeneration status
  useEffect(() => {
    if (!currentBook?.id) return undefined;
    const ref = doc(db, 'characterRegeneration', currentBook.id);
    let lastStatus = null;
    const unsub = onSnapshot(ref, (snap) => {
      const status = snap.exists() ? snap.data()?.status || null : null;
      // If Firestore says not pending, immediately clear any session flag
      if (status !== 'pending') {
        try {
          const key = `charRegen:${currentBook.id}`;
          if (typeof window !== 'undefined') window.sessionStorage.removeItem(key);
        } catch (_) {}
      }
      let isPending = status === 'pending';
      // Ignore stale pending if we're within the local ignore window
      if (isPending && Date.now() < (ignorePendingUntilRef.current || 0)) {
        isPending = false;
      }
      setRegenStatus(isPending ? 'pending' : status);
      const statusChanged = status !== lastStatus;
      if (statusChanged) lastStatus = status;
      if (isPending) {
        setRegenerating(true);
        try {
          const key = `charRegen:${currentBook.id}`;
          if (typeof window !== 'undefined') window.sessionStorage.setItem(key, 'pending');
        } catch (_) {}
      } else {
        setRegenerating(false);
        // When regeneration completes, refetch the book so UI reflects new data
        if (status === 'completed' && currentBook?.id) {
          Promise.resolve()
            .then(() => loadBook(currentBook.id))
            .then(async () => {
              // Reset stable cache to allow re-deriving lists from fresh data
              try { worldStableRef.current.world = null; } catch (_) {}
              // Pull fresh characterData immediately
              try {
                const snap = await getDoc(doc(db, 'books', currentBook.id));
                const fresh = snap.exists() ? (snap.data()?.characterData ?? null) : null;
                if (fresh != null) setCharDataOverride(fresh);
                // If current world arrays are missing/empty, UI lists are empty, or name sets differ, seed from parsed data
                try {
                  const parsed = typeof fresh === 'string' ? JSON.parse(fresh) : fresh;
                  if (parsed && typeof parsed === 'object') {
                    const toObj = (x) => (x && typeof x === 'object') ? x : { name: String(x) };
                    const chars = Array.isArray(parsed.characters || parsed.Characters) ? (parsed.characters || parsed.Characters).map(toObj) : [];
                    const placesArr = Array.isArray(parsed.locations || parsed.Locations) ? (parsed.locations || parsed.Locations).map(toObj) : [];
                    const thingsArr = Array.isArray(parsed.objects || parsed.Objects) ? (parsed.objects || parsed.Objects).map(toObj) : [];
                    const worldNow = currentBook.world || {};
                    const names = (arr) => (Array.isArray(arr) ? arr.map((x) => (typeof x === 'string' ? x : getName(x))).filter(Boolean).map((s)=>s.toLowerCase()) : []);
                    const sameSet = (a,b) => {
                      if (a.length !== b.length) return false;
                      const sa = [...new Set(a)];
                      const sb = [...new Set(b)];
                      if (sa.length !== sb.length) return false;
                      sa.sort(); sb.sort();
                      for (let i=0;i<sa.length;i++) if (sa[i]!==sb[i]) return false;
                      return true;
                    };
                    const needsChars = (
                      (!Array.isArray(worldNow.characters) || worldNow.characters.length === 0) ||
                      (Array.isArray(displayCharacters) && displayCharacters.length === 0) ||
                      !sameSet(names(worldNow.characters), names(chars))
                    ) && chars.length > 0;
                    const needsPlaces = (
                      (!Array.isArray(worldNow.places) || worldNow.places.length === 0) ||
                      (Array.isArray(displayPlaces) && displayPlaces.length === 0) ||
                      !sameSet(names(worldNow.places), names(placesArr))
                    ) && placesArr.length > 0;
                    const needsThings = (
                      (!Array.isArray(worldNow.things) || worldNow.things.length === 0) ||
                      (Array.isArray(displayThings) && displayThings.length === 0) ||
                      !sameSet(names(worldNow.things), names(thingsArr))
                    ) && thingsArr.length > 0;
                    if (needsChars || needsPlaces || needsThings) {
                      const newWorld = { ...worldNow };
                      if (needsChars) newWorld.characters = chars;
                      if (needsPlaces) newWorld.places = placesArr;
                      if (needsThings) newWorld.things = thingsArr;
                      await updateBook(currentBook.id, { world: newWorld });
                      try { worldStableRef.current.world = JSON.parse(JSON.stringify(newWorld)); } catch (_) {}
                    }
                  }
                } catch (_) {}
              } catch (_) {}
            })
            .catch(() => {});
        }
      }
    });
    return unsub;
  }, [currentBook?.id, loadBook]);

  useEffect(() => {
    if (!currentBook?.id) return;
    // Don't re-derive world while image generation is in progress
    if (isProcessing) return;
    const raw = currentBook?.characterData;
    if (!raw) return;
    const hasWorld = !!(currentBook?.world && ((Array.isArray(currentBook.world.characters) && currentBook.world.characters.length) || (Array.isArray(currentBook.world.places) && currentBook.world.places.length) || (Array.isArray(currentBook.world.things) && currentBook.world.things.length)));
    if (hasWorld) return;
    const str = typeof raw === 'string' ? raw : JSON.stringify(raw);
    const hash = hashString(str);
    if (currentBook?.worldDerivedFromCharacterDataHash === hash) return;
    let parsed = null;
    try {
      parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (_) {
      return;
    }
    const arrC = Array.isArray(parsed?.characters) ? parsed.characters : (Array.isArray(parsed?.Characters) ? parsed.Characters : []);
    const arrP = Array.isArray(parsed?.locations) ? parsed.locations : (Array.isArray(parsed?.Locations) ? parsed.Locations : []);
    const arrT = Array.isArray(parsed?.objects) ? parsed.objects : (Array.isArray(parsed?.Objects) ? parsed.Objects : []);
    const toObj = (x) => (x && typeof x === 'object') ? x : { name: String(x) };
    const world = { characters: arrC.map(toObj), places: arrP.map(toObj), things: arrT.map(toObj) };
    updateBook(currentBook.id, { world, worldDerivedFromCharacterDataHash: hash });
  }, [currentBook?.id, currentBook?.characterData, currentBook?.world, currentBook?.worldDerivedFromCharacterDataHash, updateBook, isProcessing]);

  // Derive world data from currentBook.characterData when world fields are empty
  const parsedCharacterData = useMemo(() => {
    try {
      const raw = charDataOverride != null ? charDataOverride : currentBook?.characterData;
      if (!raw) return null;
      const base = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!base || typeof base !== 'object') return base;

      const characters = base.characters || base.Characters;
      const locations = base.locations || base.Locations;
      const objects = base.objects || base.Objects;

      return {
        ...base,
        ...(characters ? { characters } : {}),
        ...(locations ? { locations } : {}),
        ...(objects ? { objects } : {}),
      };
    } catch (_) {
      return null;
    }
  }, [charDataOverride, currentBook?.characterData]);

  // When the book updates with new data, drop the override
  useEffect(() => {
    if (!currentBook?.characterData) return;
    setCharDataOverride(null);
  }, [currentBook?.id, currentBook?.characterData]);

  const derivedCharacters = useMemo(() => {
    const arr = parsedCharacterData?.characters;
    if (Array.isArray(arr)) {
      return arr
        .map((c) => (typeof c === 'string' ? c : c?.name))
        .filter(Boolean);
    }
    return [];
  }, [parsedCharacterData]);

  const derivedPlaces = useMemo(() => {
    const arr = parsedCharacterData?.locations;
    if (Array.isArray(arr)) {
      return arr
        .map((p) => (typeof p === 'string' ? p : p?.name))
        .filter(Boolean);
    }
    return [];
  }, [parsedCharacterData]);

  const derivedThings = useMemo(() => {
    const arr = parsedCharacterData?.objects;
    if (Array.isArray(arr)) {
      return arr
        .map((t) => (typeof t === 'string' ? t : t?.name))
        .filter(Boolean);
    }
    return [];
  }, [parsedCharacterData]);

  // Normalize characters into objects for card rendering
  // Prefer rich objects from parsedCharacterData when current book arrays contain only names
  const normalizedCharacters = useMemo(() => {
    const worldArr = Array.isArray(worldForDisplay?.characters) ? worldForDisplay.characters : [];
    const legacyArr = Array.isArray(currentBook?.characters) ? currentBook.characters : [];
    const parsedArr = Array.isArray(parsedCharacterData?.characters) ? parsedCharacterData.characters : [];

    const hasRichObjects = (arr) => Array.isArray(arr) && arr.some((c) => typeof c === 'object' && c && Object.keys(c).some((k) => k !== 'name'));
    const allStrings = (arr) => Array.isArray(arr) && arr.length > 0 && arr.every((c) => typeof c === 'string');

    let source = [];
    if (hasRichObjects(parsedArr)) {
      source = parsedArr;
    } else if (hasRichObjects(worldArr)) {
      source = worldArr;
    } else if (hasRichObjects(legacyArr)) {
      source = legacyArr;
    } else if (allStrings(worldArr) && parsedArr.length) {
      source = parsedArr;
    } else if (allStrings(legacyArr) && parsedArr.length) {
      source = parsedArr;
    } else if (worldArr.length) {
      source = worldArr;
    } else if (legacyArr.length) {
      source = legacyArr;
    } else {
      source = parsedArr;
    }

    return (Array.isArray(source) ? source : []).map((c) => (typeof c === 'string' ? { name: c } : c || {}));
  }, [currentBook?.world?.characters, currentBook?.characters, parsedCharacterData?.characters]);

  // Strongly prefer parsed characters for cards (ensures attributes render)
  const richCharacters = useMemo(() => {
    const arr = Array.isArray(parsedCharacterData?.characters) ? parsedCharacterData.characters : [];
    return arr.map((c) => flattenCharacter(typeof c === 'string' ? { name: c } : c || {}));
  }, [parsedCharacterData?.characters]);

  // Choose display data for character cards; always prefer world.characters to maintain UID-based order
  const displayCharacters = useMemo(() => {
    const worldArr = Array.isArray(currentBook?.world?.characters) ? currentBook.world.characters : [];
    if (worldArr.length) {
      return worldArr.map((c) => {
        const obj = typeof c === 'string' ? { name: c } : c || {};
        if (!obj._uid) {
          obj._uid = generateElementUid(obj);
        }
        return obj;
      });
    }
    const base = richCharacters.length ? richCharacters : normalizedCharacters;
    return base.map((c) => {
      const obj = typeof c === 'string' ? { name: c } : c || {};
      if (!obj._uid) {
        obj._uid = generateElementUid(obj);
      }
      return obj;
    });
  }, [currentBook?.world?.characters, richCharacters, normalizedCharacters]);

  // Heuristic safety: clear pending UI only after pending ends
  useEffect(() => {
    const hasCharacters = Array.isArray(displayCharacters) && displayCharacters.length > 0;
    if (hasCharacters && regenStatus !== 'pending' && !regenerating) {
      setRegenStatus(null);
      setRegenerating(false);
      try {
        const key = `charRegen:${currentBook?.id || bookId}`;
        if (typeof window !== 'undefined') window.sessionStorage.removeItem(key);
      } catch (_) {}
    }
  }, [displayCharacters, regenStatus, regenerating, currentBook?.id, bookId]);

  // PLACES: normalize and choose display data
  const normalizedPlacesObjects = useMemo(() => {
    const worldArr = Array.isArray(worldForDisplay?.places) ? worldForDisplay.places : [];
    const legacyArr = Array.isArray(currentBook?.places) ? currentBook.places : [];
    const parsedArr = Array.isArray(parsedCharacterData?.locations) ? parsedCharacterData.locations : [];
    const hasRichObjects = (arr) => Array.isArray(arr) && arr.some((p) => typeof p === 'object' && p && Object.keys(p).some((k) => k !== 'name'));
    const allStrings = (arr) => Array.isArray(arr) && arr.length > 0 && arr.every((p) => typeof p === 'string');
    let source = [];
    if (hasRichObjects(parsedArr)) source = parsedArr;
    else if (hasRichObjects(worldArr)) source = worldArr;
    else if (hasRichObjects(legacyArr)) source = legacyArr;
    else if (allStrings(worldArr) && parsedArr.length) source = parsedArr;
    else if (allStrings(legacyArr) && parsedArr.length) source = parsedArr;
    else if (worldArr.length) source = worldArr;
    else if (legacyArr.length) source = legacyArr;
    else source = parsedArr;
    return (Array.isArray(source) ? source : []).map((p) => (typeof p === 'string' ? { name: p } : p || {}));
  }, [currentBook?.world?.places, currentBook?.places, parsedCharacterData?.locations]);

  const richPlaces = useMemo(() => {
    const arr = Array.isArray(parsedCharacterData?.locations) ? parsedCharacterData.locations : [];
    return arr.map((p) => flattenPlace(typeof p === 'string' ? { name: p } : p || {}));
  }, [parsedCharacterData?.locations]);

  const displayPlaces = useMemo(() => {
    const worldArr = Array.isArray(currentBook?.world?.places) ? currentBook.world.places : [];
    if (worldArr.length) {
      return worldArr.map((p) => {
        const obj = flattenPlace(typeof p === 'string' ? { name: p } : p || {});
        if (!obj._uid) {
          obj._uid = generateElementUid(obj);
        }
        return obj;
      });
    }
    const base = richPlaces.length ? richPlaces : normalizedPlacesObjects;
    return base.map((p) => {
      const obj = flattenPlace(typeof p === 'string' ? { name: p } : p || {});
      if (!obj._uid) {
        obj._uid = generateElementUid(obj);
      }
      return obj;
    });
  }, [currentBook?.world?.places, richPlaces, normalizedPlacesObjects]);

  // THINGS: normalize and choose display data
  const normalizedThingsObjects = useMemo(() => {
    const worldArr = Array.isArray(worldForDisplay?.things) ? worldForDisplay.things : [];
    const legacyArr = Array.isArray(currentBook?.items) ? currentBook.items : [];
    const parsedArr = Array.isArray(parsedCharacterData?.objects) ? parsedCharacterData.objects : [];
    const hasRichObjects = (arr) => Array.isArray(arr) && arr.some((t) => typeof t === 'object' && t && Object.keys(t).some((k) => k !== 'name'));
    const allStrings = (arr) => Array.isArray(arr) && arr.length > 0 && arr.every((t) => typeof t === 'string');
    let source = [];
    if (hasRichObjects(parsedArr)) source = parsedArr;
    else if (hasRichObjects(worldArr)) source = worldArr;
    else if (hasRichObjects(legacyArr)) source = legacyArr;
    else if (allStrings(worldArr) && parsedArr.length) source = parsedArr;
    else if (allStrings(legacyArr) && parsedArr.length) source = parsedArr;
    else if (worldArr.length) source = worldArr;
    else if (legacyArr.length) source = legacyArr;
    else source = parsedArr;
    return (Array.isArray(source) ? source : []).map((t) => (typeof t === 'string' ? { name: t } : t || {}));
  }, [currentBook?.world?.things, currentBook?.items, parsedCharacterData?.objects]);

  const richThings = useMemo(() => {
    const arr = Array.isArray(parsedCharacterData?.objects) ? parsedCharacterData.objects : [];
    return arr.map((t) => flattenThing(typeof t === 'string' ? { name: t } : t || {}));
  }, [parsedCharacterData?.objects]);

  const displayThings = useMemo(() => {
    const worldArr = Array.isArray(currentBook?.world?.things) ? currentBook.world.things : [];
    if (worldArr.length) {
      return worldArr.map((t) => {
        const obj = flattenThing(typeof t === 'string' ? { name: t } : t || {});
        if (!obj._uid) {
          obj._uid = generateElementUid(obj);
        }
        return obj;
      });
    }
    const base = richThings.length ? richThings : normalizedThingsObjects;
    return base.map((t) => {
      const obj = flattenThing(typeof t === 'string' ? { name: t } : t || {});
      if (!obj._uid) {
        obj._uid = generateElementUid(obj);
      }
      return obj;
    });
  }, [currentBook?.world?.things, richThings, normalizedThingsObjects]);

  const importantObjects = useMemo(() => {
    const result = [];
    const chars = Array.isArray(displayCharacters) ? displayCharacters : [];
    const plcs = Array.isArray(displayPlaces) ? displayPlaces : [];
    const thgs = Array.isArray(displayThings) ? displayThings : [];
    
    chars.forEach((c) => {
      if (c?.important === true) {
        result.push({ ...c, _objectType: 'character' });
      }
    });
    plcs.forEach((p) => {
      if (p?.important === true) {
        result.push({ ...p, _objectType: 'place' });
      }
    });
    thgs.forEach((t) => {
      if (t?.important === true) {
        result.push({ ...t, _objectType: 'thing' });
      }
    });
    
    return result;
  }, [displayCharacters, displayPlaces, displayThings]);

  // Debug: verify what we are rendering for character cards
  useEffect(() => {
    if (activeTab === 'characters') {
      const sample = (richCharacters.length ? richCharacters : normalizedCharacters)[0];
      console.log('[WorldPage] Characters source:', {
        richCount: richCharacters.length,
        normalizedCount: normalizedCharacters.length,
        sample,
      });
    }
  }, [activeTab, richCharacters, normalizedCharacters]);

  const missingImagesCount = useMemo(() => {
    if (activeTab === 'important') {
      const arr = importantObjects || [];
      return arr.filter((obj) => !obj?.referenceImage && !isObjectImageInProcess(obj._uid)).length;
    }
    if (activeTab === 'characters') {
      const arr = Array.isArray(displayCharacters) ? displayCharacters : [];
      return arr.filter((c) => !c?.referenceImage && !isObjectImageInProcess(c._uid)).length;
    }
    if (activeTab === 'places') {
      const arr = Array.isArray(displayPlaces) ? displayPlaces : [];
      return arr.filter((p) => !p?.referenceImage && !isObjectImageInProcess(p._uid)).length;
    }
    if (activeTab === 'things') {
      const arr = Array.isArray(displayThings) ? displayThings : [];
      return arr.filter((t) => !t?.referenceImage && !isObjectImageInProcess(t._uid)).length;
    }
    return 0;
  }, [activeTab, importantObjects, displayCharacters, displayPlaces, displayThings, pendingJobs, requestingImageUids]);

  const overwriteImagesCount = useMemo(() => {
    if (activeTab === 'important') {
      const arr = importantObjects || [];
      return arr.filter((obj) => !!obj?.referenceImage && !isObjectImageInProcess(obj._uid)).length;
    }
    if (activeTab === 'characters') {
      const arr = Array.isArray(displayCharacters) ? displayCharacters : [];
      return arr.filter((c) => !!c?.referenceImage && !isObjectImageInProcess(c._uid)).length;
    }
    if (activeTab === 'places') {
      const arr = Array.isArray(displayPlaces) ? displayPlaces : [];
      return arr.filter((p) => !!p?.referenceImage && !isObjectImageInProcess(p._uid)).length;
    }
    if (activeTab === 'things') {
      const arr = Array.isArray(displayThings) ? displayThings : [];
      return arr.filter((t) => !!t?.referenceImage && !isObjectImageInProcess(t._uid)).length;
    }
    return 0;
  }, [activeTab, importantObjects, displayCharacters, displayPlaces, displayThings, pendingJobs, requestingImageUids]);

  // When any card is in edit mode, disable image generation actions
  const isAnyEditing = useMemo(() => {
    try {
      return (
        (editing && Object.keys(editing).length > 0) ||
        (editingPlaces && Object.keys(editingPlaces).length > 0) ||
        (editingThings && Object.keys(editingThings).length > 0)
      );
    } catch (_) {
      return false;
    }
  }, [editing, editingPlaces, editingThings]);

  // Calculate completed and pending job counts for bulk generation modal
  const { completedJobsCount, pendingJobsCount } = useMemo(() => {
    let completed = 0;
    let pending = 0;
    
    if (worldObjectJobs && worldObjectJobs.size > 0) {
      worldObjectJobs.forEach((job) => {
        if (job.status === 'completed') {
          completed++;
        } else if (job.status === 'pending') {
          pending++;
        }
      });
    }
    
    return { completedJobsCount: completed, pendingJobsCount: pending };
  }, [worldObjectJobs]);

  // Track when we've seen pending jobs in this session (per tab)
  useEffect(() => {
    const currentBulkState = getBulkState(activeTab);
    if (currentBulkState.generating && pendingJobsCount > 0 && !currentBulkState.hasSeenPendingJobs) {
      setBulkState(activeTab, { hasSeenPendingJobs: true });
    }
  }, [activeTab, pendingJobsCount]);

  // When all pending jobs are complete (after we've seen them), stop the bulk generation state (per tab)
  useEffect(() => {
    const currentBulkState = getBulkState(activeTab);
    if (currentBulkState.generating && currentBulkState.hasSeenPendingJobs && pendingJobsCount === 0) {
      setBulkState(activeTab, { generating: false, hasSeenPendingJobs: false, generatingMode: null });
    }
  }, [activeTab, pendingJobsCount]);



  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-600">{error}</div>
    );
  }

  const characters =
    currentBook?.world?.characters ||
    currentBook?.characters ||
    (derivedCharacters.length ? derivedCharacters : []);

  const places =
    currentBook?.world?.places ||
    currentBook?.places ||
    (derivedPlaces.length ? derivedPlaces : []);

  const things =
    currentBook?.world?.things ||
    currentBook?.items ||
    (derivedThings.length ? derivedThings : []);

  const tabs = [
    { key: 'important', label: 'Important', items: importantObjects },
    { key: 'characters', label: 'Characters', items: Array.isArray(characters) ? characters : [] },
    { key: 'places', label: 'Places', items: Array.isArray(places) ? places : [] },
    { key: 'things', label: 'Things', items: Array.isArray(things) ? things : [] },
  ];
  const currentTab = tabs.find((t) => t.key === activeTab) || tabs[0];
  const safeItems = Array.isArray(currentTab?.items) ? currentTab.items : [];
  const isImportantTab = currentTab?.key === 'important';
  const isCharactersTab = currentTab?.key === 'characters';
  const isPlacesTab = currentTab?.key === 'places';
  const isThingsTab = currentTab?.key === 'things';


  // Editing helpers
  const startEdit = (idx, c) => {
    setEditing((prev) => ({ ...prev, [idx]: { ...c } }));
  };
  // Single-generate modal helpers
  const openSingleDialog = (type, idx) => {
    setSingleDialog({ open: true, type, idx });
  };
  const closeSingleDialog = () => {
    setSingleDialog({ open: false, type: null, idx: null });
  };
  const confirmSingleGenerate = () => {
    if (!singleDialog.open) return;
    const { type, idx } = singleDialog;
    const style = singleSelectedVisualStyle || undefined;
    // Close immediately; start generation in background
    closeSingleDialog();
    // Notify user that generation has started
    try {
      const kind = type === 'character' ? 'Character' : type === 'place' ? 'Place' : 'Thing';
      notify.success(`Generating ${kind} image started. You can continue working.`, { toastId: `world-${type}-image-start`, autoClose: 4000 });
    } catch (_) {}
    const run = type === 'character'
      ? generateCharacterImage
      : type === 'place'
        ? generatePlaceImage
        : generateThingImage;
    Promise.resolve()
      .then(() => run(idx, style))
      .catch(() => {});
  };
  // IMAGE generation helpers
  const buildDetailString = (obj, omit = []) => {
    try {
      const entries = Object.entries(obj || {}).filter(([k, v]) => !omit.includes(k) && v != null && typeof v !== 'object');
      if (entries.length === 0) return '';
      return entries.map(([k, v]) => `${k}: ${String(v)}`).join(', ');
    } catch (_) { return ''; }
  };

  const handleRegenerateFromStory = async () => {
    if (!currentBook?.id) return;
    try {
      // Immediately reflect pending state in UI
      setRegenerating(true);
      setRegenStatus('pending');
      try {
        const key = `charRegen:${currentBook?.id || bookId}`;
        if (typeof window !== 'undefined') window.sessionStorage.setItem(key, 'pending');
      } catch (_) {}
      const functions = getFunctions();
      const startRegen = httpsCallable(functions, 'regenerateCharacterDataStart', { timeout: 300000 });
      const resp = await startRegen({ bookId: currentBook.id });
      const d = JSON.parse(JSON.stringify(resp?.data || {}));
      if (d && d.success) {
        await loadBook(currentBook.id);
        // Locally clear pending UI in case no status doc is written
        setRegenStatus(null);
        setRegenerating(false);
        try {
          const key = `charRegen:${currentBook?.id || bookId}`;
          if (typeof window !== 'undefined') window.sessionStorage.removeItem(key);
        } catch (_) {}
        // For the next short period, ignore any stale 'pending' status doc
        ignorePendingUntilRef.current = Date.now() + 120000; // 2 minutes
        // Force UI to recompute world from updated characterData
        try {
          worldStableRef.current.world = null;
        } catch (_) {}
        // Fetch fresh characterData immediately so UI updates without manual refresh
        try {
          const snap = await getDoc(doc(db, 'books', currentBook.id));
          const fresh = snap.exists() ? (snap.data()?.characterData ?? null) : null;
          if (fresh != null) setCharDataOverride(fresh);
          // Also persist world arrays for the active tab if they are currently empty
          try {
            const parsed = typeof fresh === 'string' ? JSON.parse(fresh) : fresh;
            if (parsed && typeof parsed === 'object') {
              const toObj = (x) => (x && typeof x === 'object') ? x : { name: String(x) };
              const chars = Array.isArray(parsed.characters || parsed.Characters) ? (parsed.characters || parsed.Characters).map(toObj) : [];
              const placesArr = Array.isArray(parsed.locations || parsed.Locations) ? (parsed.locations || parsed.Locations).map(toObj) : [];
              const thingsArr = Array.isArray(parsed.objects || parsed.Objects) ? (parsed.objects || parsed.Objects).map(toObj) : [];
              const worldNow = currentBook.world || {};
              const wantsChars = activeTab === 'characters' && (!Array.isArray(worldNow.characters) || worldNow.characters.length === 0) && chars.length > 0;
              const wantsPlaces = activeTab === 'places' && (!Array.isArray(worldNow.places) || worldNow.places.length === 0) && placesArr.length > 0;
              const wantsThings = activeTab === 'things' && (!Array.isArray(worldNow.things) || worldNow.things.length === 0) && thingsArr.length > 0;
              if (wantsChars || wantsPlaces || wantsThings) {
                const newWorld = { ...worldNow };
                if (wantsChars) newWorld.characters = chars;
                if (wantsPlaces) newWorld.places = placesArr;
                if (wantsThings) newWorld.things = thingsArr;
                await updateBook(currentBook.id, { world: newWorld });
                // Update stable cache to reflect persisted world immediately
                worldStableRef.current.world = JSON.parse(JSON.stringify(newWorld));
              }
            }
          } catch (_) {}
        } catch (_) {}

        // Background poll: if backend takes a moment to write new characterData, poll a few times
        try {
          const prevRaw = currentBook?.characterData;
          const prevStr = prevRaw ? (typeof prevRaw === 'string' ? prevRaw : JSON.stringify(prevRaw)) : '';
          const prevHash = hashString(prevStr);
          (async () => {
            for (let attempt = 0; attempt < 15; attempt++) { // ~30s @ 2s interval
              await new Promise((r) => setTimeout(r, 2000));
              try {
                const s = await getDocFromServer(doc(db, 'books', currentBook.id));
                if (!s.exists()) continue;
                const nowRaw = s.data()?.characterData ?? null;
                const nowStr = nowRaw ? (typeof nowRaw === 'string' ? nowRaw : JSON.stringify(nowRaw)) : '';
                const nowHash = hashString(nowStr);
                if (nowHash && nowHash !== prevHash) {
                  setCharDataOverride(nowRaw);
                  try { worldStableRef.current.world = null; } catch (_) {}
                  // Optionally seed world if arrays missing
                  try {
                    const parsed = typeof nowRaw === 'string' ? JSON.parse(nowRaw) : nowRaw;
                    if (parsed && typeof parsed === 'object') {
                      const toObj = (x) => (x && typeof x === 'object') ? x : { name: String(x) };
                      const chars = Array.isArray(parsed.characters || parsed.Characters) ? (parsed.characters || parsed.Characters).map(toObj) : [];
                      const placesArr = Array.isArray(parsed.locations || parsed.Locations) ? (parsed.locations || parsed.Locations).map(toObj) : [];
                      const thingsArr = Array.isArray(parsed.objects || parsed.Objects) ? (parsed.objects || parsed.Objects).map(toObj) : [];
                      const worldNow = currentBook.world || {};
                      const needsChars = !Array.isArray(worldNow.characters) || worldNow.characters.length === 0;
                      const needsPlaces = !Array.isArray(worldNow.places) || worldNow.places.length === 0;
                      const needsThings = !Array.isArray(worldNow.things) || worldNow.things.length === 0;
                      if (needsChars || needsPlaces || needsThings) {
                        const newWorld = { ...worldNow };
                        if (needsChars) newWorld.characters = chars;
                        if (needsPlaces) newWorld.places = placesArr;
                        if (needsThings) newWorld.things = thingsArr;
                        await updateBook(currentBook.id, { world: newWorld });
                        try { worldStableRef.current.world = JSON.parse(JSON.stringify(newWorld)); } catch (_) {}
                      }
                    }
                  } catch (_) {}
                  break;
                }
              } catch (_) {}
            }
          })();
        } catch (_) {}
      }
    } catch (e) {
      console.error('[WorldPage] regenerate error', e);
      try {
        const key = `charRegen:${currentBook?.id || bookId}`;
        if (typeof window !== 'undefined') window.sessionStorage.removeItem(key);
      } catch (_) {}
      setRegenStatus(null);
      setRegenerating(false);
    }
  };

  const handleRegenerateCharactersOnly = async () => {
    if (!currentBook?.id) return;
    try {
      setRegenerating(true);
      setRegenStatus('pending');
      const functions = getFunctions();
      const regen = httpsCallable(functions, 'regenerateCharactersOnlyStart', { timeout: 300000 });
      const resp = await regen({ bookId: currentBook.id });
      const d = JSON.parse(JSON.stringify(resp?.data || {}));
      if (d && d.success) {
        await loadBook(currentBook.id);
        setRegenStatus(null);
        setRegenerating(false);
        ignorePendingUntilRef.current = Date.now() + 120000;
        try { worldStableRef.current.world = null; } catch (_) {}
        // Fetch fresh characterData and seed characters into world
        try {
          const snap = await getDoc(doc(db, 'books', currentBook.id));
          const fresh = snap.exists() ? (snap.data()?.characterData ?? null) : null;
          if (fresh != null) setCharDataOverride(fresh);
          try {
            const parsed = typeof fresh === 'string' ? JSON.parse(fresh) : fresh;
            if (parsed && typeof parsed === 'object') {
              const toObj = (x) => (x && typeof x === 'object') ? x : { name: String(x) };
              const chars = Array.isArray(parsed.characters || parsed.Characters) ? (parsed.characters || parsed.Characters).map(toObj) : [];
              if (chars.length > 0) {
                const worldNow = currentBook.world || {};
                const newWorld = { ...worldNow, characters: chars };
                await updateBook(currentBook.id, { world: newWorld });
                worldStableRef.current.world = JSON.parse(JSON.stringify(newWorld));
              }
            }
          } catch (_) {}
        } catch (_) {}
      }
    } catch (e) {
      console.error('[WorldPage] regenerate characters only error', e);
      setRegenStatus(null);
      setRegenerating(false);
    }
  };

  const handleRegeneratePlacesOnly = async () => {
    if (!currentBook?.id) return;
    // Show loading UI immediately to prevent duplicate clicks
    setPlacesRegenStatus('pending');
    try {
      const functions = getFunctions();
      const regen = httpsCallable(functions, 'regeneratePlacesOnlyStart', { timeout: 300000 });
      const resp = await regen({ bookId: currentBook.id });
      const d = JSON.parse(JSON.stringify(resp?.data || {}));
      if (d && d.success) {
        // Fire-and-forget: status document creation and webhook will handle the rest
        // The real-time listener will update placesRegenStatus and reload book data
        ignorePendingUntilRef.current = Date.now() + 120000;
        try { worldStableRef.current.world = null; } catch (_) {}
      }
    } catch (e) {
      console.error('[WorldPage] regenerate places only error', e);
      setPlacesRegenStatus(null);
    }
  };

  const handleRegenerateThingsOnly = async () => {
    if (!currentBook?.id) return;
    // Show loading UI immediately to prevent duplicate clicks
    setThingsRegenStatus('pending');
    try {
      const functions = getFunctions();
      const regen = httpsCallable(functions, 'regenerateThingsOnlyStart', { timeout: 300000 });
      const resp = await regen({ bookId: currentBook.id });
      const d = JSON.parse(JSON.stringify(resp?.data || {}));
      if (d && d.success) {
        // Fire-and-forget: status document creation and webhook will handle the rest
        // The real-time listener will update thingsRegenStatus and reload book data
        ignorePendingUntilRef.current = Date.now() + 120000;
        try { worldStableRef.current.world = null; } catch (_) {}
      }
    } catch (e) {
      console.error('[WorldPage] regenerate things only error', e);
      setThingsRegenStatus(null);
    }
  };

  const generateCharacterImage = async (idx, styleOverride) => {
    if (!currentBook?.id) return;
    // Before generating, ensure character data is fully seeded into world.characters with full nested data from parsedCharacterData
    // This uses aggressive seeding - checks if parsed character names match world character names
    try {
      const parsed = parsedCharacterData;
      if (parsed && Array.isArray(parsed.characters)) {
        const chars = parsed.characters.map((c) => (c && typeof c === 'object') ? c : { name: String(c) });
        const worldNow = currentBook.world || {};
        
        // Helper functions for name comparison
        const names = (arr) => (Array.isArray(arr) ? arr.map((x) => (typeof x === 'string' ? x : getName(x))).filter(Boolean).map((s) => s.toLowerCase()) : []);
        const sameSet = (a, b) => {
          if (a.length !== b.length) return false;
          const sa = [...new Set(a)];
          const sb = [...new Set(b)];
          if (sa.length !== sb.length) return false;
          sa.sort();
          sb.sort();
          for (let i = 0; i < sa.length; i++) if (sa[i] !== sb[i]) return false;
          return true;
        };
        
        // Check if world characters need to be updated
        const needsChars = (
          (!Array.isArray(worldNow.characters) || worldNow.characters.length === 0) ||
          !sameSet(names(worldNow.characters), names(chars))
        ) && chars.length > 0;
        
        if (needsChars) {
          const newWorld = { ...worldNow, characters: chars };
          await updateBook(currentBook.id, { world: newWorld });
          // Force UI to recognize the update
          try {
            worldStableRef.current.world = JSON.parse(JSON.stringify(newWorld));
          } catch (_) {}
        }
      }
    } catch (_) {}
    
    // idx is actually a UID string, not a numeric index
    // Find the character by matching UID
    const displayChar = displayCharacters.find((c) => (c._uid || generateElementUid(c)) === idx);
    
    try {
      if (!displayChar) {
        throw new Error('Character not found in display list');
      }
      
      // Now find the same character in parsedCharacterData by name (not index)
      // This ensures we get the full nested data
      const targetName = getName(displayChar);
      const chars = parsedCharacterData?.characters || [];
      const fullCharData = chars.find((c) => getName(c) === targetName);
      
      // Use full data from parsed if available, otherwise fall back to displayChar
      const view = fullCharData || displayChar;
      
      const has = await checkCredits(GENERATION_COST);
      if (!has) {
        alert('Insufficient credits. 5 credits required.');
        return;
      }
      const { imageUrl: _omitImageUrl, referenceImage: _omitReferenceImage, ...characterNoImage } = view || {};
      const payload = {
        type: 'character',
        character: characterNoImage,
      };
      const artStyle = styleOverride || currentBook?.visualStyle?.value || currentBook?.visualStyle || 'Digital SciFi Art';
      const resp = await generateCharacterImageByJSON(artStyle, JSON.stringify(payload));
      const d = JSON.parse(JSON.stringify(resp || {}));
      const resultText = typeof d.result === 'undefined' ? null : d.result;
      let url = null;
      if (typeof resultText === 'string') {
        if (resultText.startsWith('http')) {
          url = resultText.trim();
        } else {
          try {
            const obj = JSON.parse(resultText);
            url = obj?.imageUrl || obj?.url || null;
          } catch (_) {}
          if (!url) {
            const idxHttp = resultText.indexOf('http');
            if (idxHttp >= 0) {
              const end = resultText.indexOf(' ', idxHttp);
              url = end >= 0 ? resultText.slice(idxHttp, end) : resultText.slice(idxHttp);
            }
          }
        }
      }
      if (!url && typeof d.imageUrl === 'string') {
        url = d.imageUrl;
      }
      if (!url) {
        throw new Error('No image URL returned');
      }
      const currentWorld = Array.isArray(currentBook?.world?.characters) ? currentBook.world.characters : null;
      let updated;
      if (Array.isArray(currentWorld) && currentWorld.length > 0) {
        const targetName = getName(view);
        updated = currentWorld.map((c) => (getName(c) === targetName ? { ...c, referenceImage: url } : c));
      } else {
        updated = displayCharacters.map((c, i) => (i === idx ? { ...c, referenceImage: url } : c));
      }
      const newWorld = { ...(currentBook.world || {}), characters: updated };
      await updateBook(currentBook.id, { world: newWorld });
      // Charged server-side by generate*ImageByJSON (helpers/worldObjectImage.js).
    } catch (err) {
      console.error('[WorldPage] generateCharacterImage error', err);
      const charName = displayChar ? getName(displayChar) : 'Unknown';
      notify.error(`Failed to generate image for character "${charName}" - credits refunded`, { toastId: `world-character-image-error-${charName}` });
    }
  };

  const generatePlaceImage = async (uid, styleOverride) => {
    if (!currentBook?.id) return;
    const view = (currentBook?.world?.places || []).find(p => (p._uid || generateElementUid(p)) === uid);
    try {
      const has = await checkCredits(GENERATION_COST);
      if (!has) {
        alert('Insufficient credits. 5 credits required.');
        return;
      }
      const { imageUrl: _omitImageUrl, referenceImage: _omitReferenceImage, ...placeNoImage } = view || {};
      const payload = {
        type: 'place',
        place: placeNoImage,
      };
      const artStyle = styleOverride || currentBook?.visualStyle?.value || currentBook?.visualStyle || 'Digital SciFi Art';
      const resp = await generatePlaceImageByJSON(artStyle, JSON.stringify(payload));
      const d = JSON.parse(JSON.stringify(resp || {}));
      const resultText = typeof d.result === 'undefined' ? null : d.result;
      let url = null;
      if (typeof resultText === 'string') {
        if (resultText.startsWith('http')) {
          url = resultText.trim();
        } else {
          try {
            const obj = JSON.parse(resultText);
            url = obj?.imageUrl || obj?.url || null;
          } catch (_) {}
          if (!url) {
            const idxHttp = resultText.indexOf('http');
            if (idxHttp >= 0) {
              const end = resultText.indexOf(' ', idxHttp);
              url = end >= 0 ? resultText.slice(idxHttp, end) : resultText.slice(idxHttp);
            }
          }
        }
      }
      if (!url && typeof d.imageUrl === 'string') {
        url = d.imageUrl;
      }
      if (!url) {
        throw new Error('No image URL returned');
      }
      const currentWorld = ensureWorldElementsHaveUids(currentBook?.world || {});
      const updated = (currentWorld.places || []).map((p) => {
        if (p._uid === uid) {
          const imageHistory = p.imageHistory || [];
          const newHistory = [
            { url, generatedAt: new Date().toISOString(), artStyle },
            ...imageHistory.slice(0, 9),
          ];
          return { ...p, referenceImage: url, imageHistory: newHistory };
        }
        return p;
      });
      const newWorld = { ...currentWorld, places: updated };
      await updateBook(currentBook.id, { world: newWorld });
      // Charged server-side by generate*ImageByJSON (helpers/worldObjectImage.js).
    } catch (err) {
      console.error('[WorldPage] generatePlaceImage error', err);
      const placeName = view ? getName(view) : 'Unknown';
      notify.error(`Failed to generate image for place "${placeName}" - credits refunded`, { toastId: `world-place-image-error-${placeName}` });
    }
  };

  const generateThingImage = async (uid, styleOverride) => {
    if (!currentBook?.id) return;
    const view = (currentBook?.world?.things || []).find(t => (t._uid || generateElementUid(t)) === uid);
    try {
      const has = await checkCredits(GENERATION_COST);
      if (!has) {
        alert('Insufficient credits. 5 credits required.');
        return;
      }
      const { imageUrl: _omitImageUrl, referenceImage: _omitReferenceImage, ...thingNoImage } = view || {};
      const payload = {
        type: 'thing',
        thing: thingNoImage,
      };
      const artStyle = styleOverride || currentBook?.visualStyle?.value || currentBook?.visualStyle || 'Digital SciFi Art';
      const resp = await generateThingImageByJSON(artStyle, JSON.stringify(payload));
      const d = JSON.parse(JSON.stringify(resp || {}));
      const resultText = typeof d.result === 'undefined' ? null : d.result;
      let url = null;
      if (typeof resultText === 'string') {
        if (resultText.startsWith('http')) {
          url = resultText.trim();
        } else {
          try {
            const obj = JSON.parse(resultText);
            url = obj?.imageUrl || obj?.url || null;
          } catch (_) {}
          if (!url) {
            const idxHttp = resultText.indexOf('http');
            if (idxHttp >= 0) {
              const end = resultText.indexOf(' ', idxHttp);
              url = end >= 0 ? resultText.slice(idxHttp, end) : resultText.slice(idxHttp);
            }
          }
        }
      }
      if (!url && typeof d.imageUrl === 'string') {
        url = d.imageUrl;
      }
      if (!url) {
        throw new Error('No image URL returned');
      }
      const currentWorld = ensureWorldElementsHaveUids(currentBook?.world || {});
      const updated = (currentWorld.things || []).map((t) => {
        if (t._uid === uid) {
          const imageHistory = t.imageHistory || [];
          const newHistory = [
            { url, generatedAt: new Date().toISOString(), artStyle },
            ...imageHistory.slice(0, 9),
          ];
          return { ...t, referenceImage: url, imageHistory: newHistory };
        }
        return t;
      });
      const newWorld = { ...currentWorld, things: updated };
      await updateBook(currentBook.id, { world: newWorld });
      // Charged server-side by generate*ImageByJSON (helpers/worldObjectImage.js).
    } catch (err) {
      console.error('[WorldPage] generateThingImage error', err);
      const thingName = view ? getName(view) : 'Unknown';
      notify.error(`Failed to generate image for thing "${thingName}" - credits refunded`, { toastId: `world-thing-image-error-${thingName}` });
    }
  };

  // PLACES editing helpers
  const startEditPlace = (idx, p) => {
    setEditingPlaces((prev) => ({ ...prev, [idx]: { ...p } }));
  };
  const cancelEditPlace = (idx) => {
    setEditingPlaces((prev) => {
      const next = { ...prev };
      delete next[idx];
      return next;
    });
  };
  const onPlaceAttrChange = (idx, key, value) => {
    setEditingPlaces((prev) => ({ ...prev, [idx]: { ...(prev[idx] || {}), [key]: value } }));
  };
  const onDeletePlaceAttr = (idx, path) => {
    setEditingPlaces((prev) => {
      const cur = { ...(prev[idx] || {}) };
      const list = Array.isArray(cur.__delete__) ? cur.__delete__ : [];
      if (!list.includes(path)) list.push(path);
      cur.__delete__ = list;
      if (Object.prototype.hasOwnProperty.call(cur, path)) {
        const { [path]: _omit, ...rest } = cur;
        return { ...prev, [idx]: rest };
      }
      return { ...prev, [idx]: cur };
    });
  };
  const onAddPlaceAttr = (idx) => {
    const key = String(addPlaceField[idx] || '').trim();
    if (!key) return;
    setEditingPlaces((prev) => ({
      ...prev,
      [idx]: { ...(prev[idx] || {}), [key]: addPlaceValue[idx] ?? '' }
    }));
    setAddPlaceField((p) => ({ ...p, [idx]: '' }));
    setAddPlaceValue((p) => ({ ...p, [idx]: '' }));
  };
  const savePlaceEdit = async (uid) => {
    if (!currentBook?.id || !editingPlaces[uid]) return;
    try {
      setSavingPlaceUid(uid);
      const currentWorld = ensureWorldElementsHaveUids(currentBook?.world || {});
      const updated = (currentWorld.places || []).map((p) => 
        p._uid === uid ? mergeDotted(p, editingPlaces[uid]) : p
      );
      const newWorld = {
        ...currentWorld,
        places: updated,
      };
      await updateBook(currentBook.id, { world: newWorld });
      setEditingPlaces((prev) => {
        const next = { ...prev };
        delete next[uid];
        return next;
      });
    } catch (e) {
      console.error('[WorldPage] Failed to save place edit', e);
    } finally {
      setSavingPlaceUid(null);
    }
  };
  // THINGS editing helpers
  const startEditThing = (idx, t) => {
    setEditingThings((prev) => ({ ...prev, [idx]: { ...t } }));
  };
  const cancelEditThing = (idx) => {
    setEditingThings((prev) => {
      const next = { ...prev };
      delete next[idx];
      return next;
    });
  };
  const onThingAttrChange = (idx, key, value) => {
    setEditingThings((prev) => ({ ...prev, [idx]: { ...(prev[idx] || {}), [key]: value } }));
  };
  const onDeleteThingAttr = (idx, path) => {
    setEditingThings((prev) => {
      const cur = { ...(prev[idx] || {}) };
      const list = Array.isArray(cur.__delete__) ? cur.__delete__ : [];
      if (!list.includes(path)) list.push(path);
      cur.__delete__ = list;
      if (Object.prototype.hasOwnProperty.call(cur, path)) {
        const { [path]: _omit, ...rest } = cur;
        return { ...prev, [idx]: rest };
      }
      return { ...prev, [idx]: cur };
    });
  };
  const onAddThingAttr = (idx) => {
    const key = String(addThingField[idx] || '').trim();
    if (!key) return;
    setEditingThings((prev) => ({
      ...prev,
      [idx]: { ...(prev[idx] || {}), [key]: addThingValue[idx] ?? '' }
    }));
    setAddThingField((p) => ({ ...p, [idx]: '' }));
    setAddThingValue((p) => ({ ...p, [idx]: '' }));
  };
  const saveThingEdit = async (uid) => {
    if (!currentBook?.id || !editingThings[uid]) return;
    try {
      setSavingThingUid(uid);
      const currentWorld = ensureWorldElementsHaveUids(currentBook?.world || {});
      const updated = (currentWorld.things || []).map((t) => 
        t._uid === uid ? mergeDotted(t, editingThings[uid]) : t
      );
      const newWorld = {
        ...currentWorld,
        things: updated,
      };
      await updateBook(currentBook.id, { world: newWorld });
      setEditingThings((prev) => {
        const next = { ...prev };
        delete next[uid];
        return next;
      });
    } catch (e) {
      console.error('[WorldPage] Failed to save thing edit', e);
    } finally {
      setSavingThingUid(null);
    }
  };
  const cancelEdit = (idx) => {
    setEditing((prev) => {
      const next = { ...prev };
      delete next[idx];
      return next;
    });
  };
  const onDeleteAttr = (idx, path) => {
    setEditing((prev) => {
      const cur = { ...(prev[idx] || {}) };
      const existing = Array.isArray(cur.__delete__) ? cur.__delete__ : [];
      if (!existing.includes(path)) existing.push(path);
      cur.__delete__ = existing;
      // Also clear any directly-set edited value for this key
      if (Object.prototype.hasOwnProperty.call(cur, path)) {
        const { [path]: _omit, ...rest } = cur;
        return { ...prev, [idx]: rest };
      }
      return { ...prev, [idx]: cur };
    });
  };
  const onAddAttr = (idx) => {
    const key = String(addCharField[idx] || '').trim();
    if (!key) return;
    setEditing((prev) => ({
      ...prev,
      [idx]: { ...(prev[idx] || {}), [key]: addCharValue[idx] ?? '' }
    }));
    setAddCharField((p) => ({ ...p, [idx]: '' }));
    setAddCharValue((p) => ({ ...p, [idx]: '' }));
  };
  const onAddGroupAttr = (idx, groupKey) => {
    const k = `${idx}::${groupKey}`;
    const field = String(addGroupField[k] || '').trim();
    if (!field) return;
    const path = `${groupKey}.${field}`;
    setEditing((prev) => ({
      ...prev,
      [idx]: { ...(prev[idx] || {}), [path]: addGroupValue[k] ?? '' }
    }));
    setAddGroupField((p) => ({ ...p, [k]: '' }));
    setAddGroupValue((p) => ({ ...p, [k]: '' }));
  };
  const onAttrChange = (idx, key, value) => {
    setEditing((prev) => ({ ...prev, [idx]: { ...(prev[idx] || {}), [key]: value } }));
  };
  const isPlainObject = (val) => val && typeof val === 'object' && !Array.isArray(val);
  const deepMergeSmart = (target, source) => {
    if (!isPlainObject(target)) return JSON.parse(JSON.stringify(source));
    if (!isPlainObject(source)) return JSON.parse(JSON.stringify(source));
    const out = JSON.parse(JSON.stringify(target));
    Object.keys(source).forEach((k) => {
      const sv = source[k];
      const tv = out[k];
      if (Array.isArray(sv)) {
        out[k] = JSON.parse(JSON.stringify(sv));
      } else if (isPlainObject(sv) && isPlainObject(tv)) {
        out[k] = deepMergeSmart(tv, sv);
      } else {
        out[k] = JSON.parse(JSON.stringify(sv));
      }
    });
    return out;
  };
  const setAtPath = (obj, pathArr, value) => {
    let cur = obj;
    for (let i = 0; i < pathArr.length - 1; i++) {
      const key = pathArr[i];
      if (!isPlainObject(cur[key])) cur[key] = {};
      cur = cur[key];
    }
    const last = pathArr[pathArr.length - 1];
    if (isPlainObject(cur[last]) && isPlainObject(value)) {
      cur[last] = deepMergeSmart(cur[last], value);
    } else {
      cur[last] = value;
    }
  };
  const normalizeKeysDeep = (val) => {
    if (Array.isArray(val)) return val.map((v) => normalizeKeysDeep(v));
    if (!isPlainObject(val)) return val;
    const nonDotted = {};
    const dotted = [];
    Object.entries(val).forEach(([k, v]) => {
      if (typeof k === 'string' && k.includes('.')) dotted.push([k, v]);
      else nonDotted[k] = normalizeKeysDeep(v);
    });
    const out = nonDotted;
    dotted.forEach(([k, v]) => {
      const parts = k.split('.');
      setAtPath(out, parts, normalizeKeysDeep(v));
    });
    return out;
  };
  const deleteAtPath = (obj, parts) => {
    let cur = obj;
    for (let i = 0; i < parts.length - 1; i++) {
      const key = parts[i];
      if (!isPlainObject(cur[key])) return;
      cur = cur[key];
    }
    delete cur[parts[parts.length - 1]];
  };
  const mergeDotted = (base, edits) => {
    const toDelete = Array.isArray(edits?.__delete__) ? edits.__delete__ : [];
    const { __delete__: _omit, ...rest } = edits || {};
    const normalizedEdits = normalizeKeysDeep(rest || {});
    const merged = deepMergeSmart(base || {}, normalizedEdits);
    if (Array.isArray(toDelete) && toDelete.length) {
      toDelete.forEach((path) => {
        if (typeof path !== 'string' || !path) return;
        const parts = path.split('.');
        deleteAtPath(merged, parts);
      });
    }
    return merged;
  };
  const saveEdit = async (uid) => {
    if (!currentBook?.id || !editing[uid]) return;
    try {
      setSavingIdx(uid);
      // Find character by UID and update it
      const currentWorld = ensureWorldElementsHaveUids(currentBook?.world || {});
      const updatedChars = (currentWorld.characters || []).map((c) => 
        c._uid === uid ? mergeDotted(c, editing[uid]) : c
      );
      const newWorld = {
        ...currentWorld,
        characters: updatedChars,
      };
      await updateBook(currentBook.id, { world: newWorld });
      setEditing((prev) => {
        const next = { ...prev };
        delete next[uid];
        return next;
      });
    } catch (e) {
      console.error('[WorldPage] Failed to save character edit', e);
    } finally {
      setSavingIdx(null);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">World</h1>
        <div className="flex gap-3">
          <button
            onClick={() => setShowTutorialModal(true)}
            className="px-4 py-2 rounded-md bg-yellow-100 text-yellow-900 hover:bg-yellow-200 font-medium"
          >
            Tutorial
          </button>
          <button
            onClick={() => navigate(`/edit/${bookId}`)}
            className="px-4 py-2 rounded-md border text-gray-700 hover:bg-gray-50"
          >
            Back to Story
          </button>
          <button
            onClick={async () => {
              navigate(`/design/${bookId}`);
            }}
            disabled={!currentBook?.spreads || Object.keys(currentBook.spreads).length === 0}
            className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Continue to Design
          </button>
        </div>
      </div>

      <TutorialModal
        isOpen={showTutorialModal}
        onClose={() => setShowTutorialModal(false)}
        defaultTabId={3}
      />

      <p className="text-gray-600">Characters, places, and other elements extracted from your story. This step is optional.</p>

      <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
        <p className="text-sm text-amber-800">
          <span className="font-semibold">Tip:</span> Generating world images helps maintain visual consistency across your book&apos;s illustrations. For best results, generate images for <span className="font-semibold">ALL</span> characters, places, and things before proceeding to Design.
        </p>
      </div>

      {(regenerating || regenStatus === 'pending') && (
        <div className="mt-3 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-800 px-4 py-2 flex items-center gap-3">
          <div className="w-4 h-4 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
          <span className="flex-1">Extracting characters and world from your story... This can take a minute.</span>
          <button type="button" onClick={clearPendingUI} className="text-indigo-700 hover:text-indigo-900 text-sm font-medium">Dismiss</button>
        </div>
      )}

      <div className="w-full">
        <div className="flex items-center justify-between border-b border-gray-200 mb-4">
          <div className="flex">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`px-4 py-2 -mb-px border-b-2 transition-colors ${
                  activeTab === t.key
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {(() => {
            const currentBulkState = getBulkState(activeTab);
            return (
              <button
                onClick={() => setBulkDialogOpen(true)}
                disabled={currentBulkState.generating || isAnyEditing}
                className="px-4 py-2 mb-4 rounded-lg font-semibold text-sm text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed ml-4 shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" />
                </svg>
                {currentBulkState.generating
                  ? `Generating ${Math.min(currentBulkState.total, Math.max(0, currentBulkState.total - pendingJobsCount))}/${currentBulkState.total}`
                  : `Generate All ${isImportantTab ? 'Important Images' : isCharactersTab ? 'Character Images' : isPlacesTab ? 'Place Images' : isThingsTab ? 'Thing Images' : 'Images'}${missingImagesCount > 0 ? ` (${missingImagesCount})` : ''}`}
              </button>
            );
          })()}
        </div>

      <section className="relative bg-white rounded-lg shadow p-5 min-h-96">
        {/* Loading overlay covering world objects while generation is in progress */}
        {isProcessing && (
          <div className="absolute inset-0 z-40 bg-white/80 backdrop-blur-sm rounded-lg flex items-center justify-center pointer-events-none">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
              <p className="text-sm font-medium text-gray-700">Generating world cast, places, and objects...</p>
              {designProgress > 0 && (
                <div className="w-48">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-gray-600">Progress</span>
                    <span className="text-xs text-gray-600">{designProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className="bg-indigo-600 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${designProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {isImportantTab ? (
          importantObjects.length === 0 ? (
            <div className="text-sm text-gray-600">
              <p>No objects marked as important yet. Mark objects as important in the Characters, Places, or Things tabs to see them here.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {importantObjects.map((obj) => {
                const uid = obj._uid || generateElementUid(obj);
                const objectType = obj._objectType;
                const isEditing = editing[uid] != null || editingPlaces[uid] != null || editingThings[uid] != null;
                const view = isEditing ? (editing[uid] || editingPlaces[uid] || editingThings[uid] || obj) : obj;
                
                const typeLabel = objectType === 'character' ? 'Character' : objectType === 'place' ? 'Place' : 'Thing';
                const typeBgColor = objectType === 'character' ? 'bg-blue-100 text-blue-800' : objectType === 'place' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800';
                
                return (
                  <div key={uid} className="rounded-lg border border-gray-200 shadow-sm p-4 bg-white flex flex-col h-full">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{getName(view) || 'Unnamed'}</h3>
                        <span className={`inline-block text-xs font-medium px-2 py-1 rounded mt-1 ${typeBgColor}`}>
                          {typeLabel}
                        </span>
                      </div>
                      <button onClick={() => openModal(objectType, uid)} className="text-sm px-2 py-1 rounded border hover:bg-gray-50">Edit</button>
                    </div>
                    <div className="mb-2 w-full aspect-[3/2] bg-gray-100 rounded overflow-hidden flex items-center justify-center relative cursor-pointer" onClick={() => openModal(objectType, uid)}>
                      {pendingJobs.has(uid) ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm">
                          <div className="w-10 h-10 border-4 border-gray-300 border-t-indigo-600 rounded-full animate-spin" />
                        </div>
                      ) : null}
                      {view?.referenceImage ? (
                        <img
                          key={view.referenceImage}
                          src={view.referenceImage}
                          alt={getName(view) || 'Object image'}
                          className="w-full h-full object-contain"

                        />
                      ) : (
                        <div className="text-gray-400 text-sm">No image</div>
                      )}
                    </div>
                    {(() => {
                      try {
                        const base = view || {};
                        const toDeleteSet = new Set(Array.isArray(isEditing ? (editing[uid]?.__delete__ || editingPlaces[uid]?.__delete__ || editingThings[uid]?.__delete__) : []) ? (editing[uid]?.__delete__ || editingPlaces[uid]?.__delete__ || editingThings[uid]?.__delete__) : []);
                        const isEmptyOrNA = (val) => {
                          if (val === undefined || val === null || val === '') return true;
                          if (typeof val === 'string' && val.toLowerCase().includes('n/a')) return true;
                          return false;
                        };

                        const rawEntries = Object.entries(base).filter(([k, v]) => (
                          k !== 'name' && k !== 'Name' && k !== 'mainName' && k !== 'nickName' && k !== 'nickname' && k !== 'Alias' && k !== 'alias' && k !== 'TrueName' && k !== 'trueName' &&
                          k !== 'outfit' &&
                          k !== 'description' &&
                          k !== 'imageUrl' &&
                          k !== 'referenceImage' &&
                          k !== '__delete__' &&
                          k !== '_uid' &&
                          k !== '_objectType' &&
                          !isEmptyOrNA(v)
                        ));

                        const expanded = [];
                        rawEntries.forEach(([k, v]) => {
                          if (v && typeof v === 'object' && !Array.isArray(v)) {
                            if (toDeleteSet.has(k)) return;
                            const flattenNested = (obj, prefix = '') => {
                              Object.entries(obj).forEach(([subKey, subVal]) => {
                                if (isEmptyOrNA(subVal)) return;
                                const full = prefix ? `${prefix}.${subKey}` : `${k}.${subKey}`;
                                if (toDeleteSet.has(full)) return;
                                
                                if (typeof subVal === 'object' && !Array.isArray(subVal)) {
                                  flattenNested(subVal, full);
                                } else if (Array.isArray(subVal)) {
                                  const arrayStr = subVal
                                    .filter(item => !isEmptyOrNA(item))
                                    .map(item => {
                                      if (typeof item === 'object' && item !== null) {
                                        if (item.label) return item.label;
                                        if (item.name) return item.name;
                                        return JSON.stringify(item);
                                      }
                                      return String(item);
                                    }).join(', ');
                                  if (arrayStr) expanded.push([full, arrayStr]);
                                } else {
                                  expanded.push([full, subVal]);
                                }
                              });
                            };
                            flattenNested(v);
                          } else if (Array.isArray(v)) {
                            if (toDeleteSet.has(k)) return;
                            v.forEach((item, itemIdx) => {
                              if (isEmptyOrNA(item)) return;
                              if (typeof item === 'object' && !Array.isArray(item)) {
                                Object.entries(item).forEach(([subKey, subVal]) => {
                                  if (isEmptyOrNA(subVal)) return;
                                  const full = `${k}[${itemIdx}].${subKey}`;
                                  if (toDeleteSet.has(full)) return;
                                  
                                  if (typeof subVal === 'object' && !Array.isArray(subVal)) {
                                    const flattenNestedInArray = (obj, prefix = '') => {
                                      Object.entries(obj).forEach(([nestedKey, nestedVal]) => {
                                        if (isEmptyOrNA(nestedVal)) return;
                                        const nestedFull = prefix ? `${prefix}.${nestedKey}` : `${full}.${nestedKey}`;
                                        if (toDeleteSet.has(nestedFull)) return;
                                        
                                        if (typeof nestedVal === 'object' && !Array.isArray(nestedVal)) {
                                          flattenNestedInArray(nestedVal, nestedFull);
                                        } else if (Array.isArray(nestedVal)) {
                                          const arrayStr = nestedVal
                                            .filter(x => !isEmptyOrNA(x))
                                            .map(x => typeof x === 'object' && x !== null ? JSON.stringify(x) : String(x))
                                            .join(', ');
                                          if (arrayStr) expanded.push([nestedFull, arrayStr]);
                                        } else {
                                          expanded.push([nestedFull, nestedVal]);
                                        }
                                      });
                                    };
                                    flattenNestedInArray(subVal);
                                  } else if (Array.isArray(subVal)) {
                                    const arrayStr = subVal
                                      .filter(x => !isEmptyOrNA(x))
                                      .map(x => typeof x === 'object' && x !== null ? JSON.stringify(x) : String(x))
                                      .join(', ');
                                    if (arrayStr) expanded.push([full, arrayStr]);
                                  } else {
                                    expanded.push([full, subVal]);
                                  }
                                });
                              } else {
                                const full = `${k}[${itemIdx}]`;
                                if (!toDeleteSet.has(full)) {
                                  expanded.push([full, item]);
                                }
                              }
                            });
                          } else {
                            if (toDeleteSet.has(k)) return;
                            expanded.push([k, v]);
                          }
                        });

                        if (expanded.length === 0) return null;

                        const labelize = (s) => s
                          .replace(/\./g, ' - ')
                          .replace(/([A-Z])/g, ' $1')
                          .replace(/^./, (ch) => ch.toUpperCase());

                        const groupMap = new Map();
                        expanded.forEach(([fullKey, value]) => {
                          const dotIndex = fullKey.indexOf('.')
                          const groupKey = dotIndex === -1 ? '' : fullKey.slice(0, dotIndex);
                          const fieldKey = dotIndex === -1 ? fullKey : fullKey.slice(dotIndex + 1);
                          const arr = groupMap.get(groupKey) || [];
                          arr.push([fieldKey, value]);
                          groupMap.set(groupKey, arr);
                        });

                        const flatGroup = groupMap.get('') || [];
                        const nestedGroups = Array.from(groupMap.entries()).filter(([k]) => k !== '');

                        const uniqueFlat = (() => {
                          const seen = new Set();
                          const out = [];
                          flatGroup.forEach(([fieldKey, value]) => {
                            const norm = String(fieldKey).trim().toLowerCase();
                            if (!seen.has(norm)) {
                              seen.add(norm);
                              out.push([fieldKey, value]);
                            }
                          });
                          return out;
                        })();

                        const dedupeFields = (fields) => {
                          const seen = new Set();
                          const out = [];
                          fields.forEach(([fieldKey, value]) => {
                            const norm = String(fieldKey).trim().toLowerCase();
                            if (!seen.has(norm)) {
                              seen.add(norm);
                              out.push([fieldKey, value]);
                            }
                          });
                          return out;
                        };

                        return (
                          <div className="mt-3 text-sm flex-1 flex flex-col">
                            <div className="max-h-40 overflow-hidden">
                              <p className="font-medium text-gray-700">Attributes</p>
                              {flatGroup.length > 0 && (
                                <ul className="mt-1 space-y-1 list-disc list-inside">
                                  {uniqueFlat.map(([fieldKey, value]) => {
                                    if (typeof value === 'object' && value !== null) {
                                      return null;
                                    }
                                    return (
                                      <li key={String(fieldKey).trim().toLowerCase()} className="text-sm text-gray-600">
                                        <span className="text-gray-700">{labelize(fieldKey)}:</span>{' '}
                                        <span className="text-gray-900">{String(value)}</span>
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}

                              {nestedGroups.map(([groupKey, fields]) => (
                                <div key={groupKey} className="mt-3">
                                  <p className="font-medium text-gray-700">{labelize(groupKey)}</p>
                                  <ul className="mt-1 space-y-1 list-disc list-inside">
                                    {dedupeFields(fields).map(([fieldKey, value]) => {
                                      if (typeof value === 'object' && value !== null) {
                                        return null;
                                      }
                                      return (
                                        <li key={`${groupKey}.${String(fieldKey).trim().toLowerCase()}`} className="text-sm text-gray-600">
                                          <span className="text-gray-700">{labelize(fieldKey)}:</span>{' '}
                                          <span className="text-gray-900">{String(value)}</span>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              ))}
                            </div>
                            {(flatGroup.length > 0 || nestedGroups.length > 0) && (
                              <div className="mt-auto pt-3 flex flex-col items-center">
                                <div className="w-full border-t border-gray-200" />
                                <button
                                  type="button"
                                  onClick={() => openModal(objectType, uid)}
                                  className="mt-1 px-3 py-1 text-sm font-semibold text-indigo-600 hover:text-indigo-800 flex items-center justify-center"
                                >
                                  View more
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      } catch (_) {
                        return null;
                      }
                    })()}
                  </div>
                );
              })}
            </div>
          )
        ) : isCharactersTab ? (
          (Array.isArray(displayCharacters) && displayCharacters.length === 0) ? (
            charRegenStatus === 'pending' ? (
              <div className="text-sm text-gray-600">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-5 h-5 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
                  <p>Generating characters from your story... This may take a moment.</p>
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-1/2" />
                </div>
              </div>
            ) : charRegenStatus === 'error' ? (
              <div className="text-sm text-red-600">
                <p className="mb-2 font-semibold">Character generation failed.</p>
                <p className="mb-3">Please try again or contact support if the issue persists.</p>
                <button
                  type="button"
                  onClick={handleRegenerateCharactersOnly}
                  disabled={regenerating}
                  className="inline-flex items-center px-3 py-1.5 rounded-md border border-red-300 text-red-700 hover:bg-red-50 text-xs font-medium disabled:opacity-50"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <div className="text-sm text-gray-600">
                <p className="mb-2">
                  Something went wrong and we couldn&apos;t load your characters.
                </p>
                <button
                  type="button"
                  onClick={handleRegenerateCharactersOnly}
                  disabled={regenerating || regenStatus === 'pending'}
                  className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs font-medium disabled:opacity-50"
                >
                  {regenerating || regenStatus === 'pending' ? 'Regenerating...' : 'Generate again from Story'}
                </button>
              </div>
            )
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayCharacters.map((c) => {
                const uid = c._uid || generateElementUid(c);
                const isEditing = editing[uid] != null;
                const view = isEditing ? editing[uid] : c;
                const isExpanded = charactersExpanded;
                const toggleExpanded = () => {
                  openModal('character', uid);
                };
                const updateOutfit = (field, value) => {
                  setEditing((prev) => ({
                    ...prev,
                    [uid]: {
                      ...(prev[uid] || {}),
                      outfit: {
                        ...((prev[uid] && prev[uid].outfit) || view.outfit || {}),
                        [field]: value,
                      },
                    },
                  }));
                };
                return (
                  <div key={uid} className="rounded-lg border border-gray-200 shadow-sm p-4 bg-white flex flex-col h-full">
                    <div className="flex items-start justify-between mb-1">
                      <h3 className="font-semibold text-gray-900">{getName(view) || 'Unnamed'}</h3>
                      <button onClick={() => openModal('character', uid)} className="text-sm px-2 py-1 rounded border hover:bg-gray-50">Edit</button>
                    </div>
                    <div className="mb-2 w-full aspect-[3/2] bg-gray-100 rounded overflow-hidden flex items-center justify-center relative cursor-pointer" onClick={() => openModal('character', uid)}>
                      {pendingJobs.has(uid) ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm">
                          <div className="w-10 h-10 border-4 border-gray-300 border-t-indigo-600 rounded-full animate-spin" />
                        </div>
                      ) : null}
                      {view?.referenceImage ? (
                        <img
                          key={view.referenceImage}
                          src={view.referenceImage}
                          alt={getName(view) || 'Character image'}
                          className="w-full h-full object-contain"

                        />
                      ) : (
                        <div className="text-gray-400 text-sm">No image</div>
                      )}
                    </div>

                    {(() => {
                      try {
                        const base = view || {};
                        const toDeleteSet = new Set(Array.isArray(editing[uid]?.__delete__) ? editing[uid].__delete__ : []);
                        // Helper function to check if a value is empty or N/A
                        const isEmptyOrNA = (val) => {
                          if (val === undefined || val === null || val === '') return true;
                          if (typeof val === 'string' && val.toLowerCase().includes('n/a')) return true;
                          return false;
                        };

                        const rawEntries = Object.entries(base).filter(([k, v]) => (
                          k !== 'name' && k !== 'Name' && k !== 'mainName' && k !== 'nickName' && k !== 'nickname' && k !== 'Alias' && k !== 'alias' && k !== 'TrueName' && k !== 'trueName' &&
                          k !== 'outfit' &&
                          k !== 'imageUrl' &&
                          k !== 'referenceImage' &&
                          k !== '__delete__' &&
                          k !== '_uid' &&
                          !isEmptyOrNA(v) &&
                          (k !== 'progression' || (Array.isArray(v) && v.length > 0)) // Include progression if it's a non-empty array
                        ));

                        // Debug: log raw entries to see what we're working with
                        if (uid === c._uid) {
                          console.log('[WorldPage] Character attributes - rawEntries:', rawEntries.map(([k, v]) => [k, typeof v, v]));
                        }

                        // Expand nested object fields into their own attribute rows, e.g.
                        // facialFeatures.eyeColor, physicalAttributes.height
                        const expanded = [];
                        rawEntries.forEach(([k, v]) => {
                          if (v && typeof v === 'object' && !Array.isArray(v)) {
                            // If entire group key is marked for deletion, skip whole group
                            if (toDeleteSet.has(k)) return;
                            // Recursively flatten nested objects to any depth
                            const flattenNested = (obj, prefix = '') => {
                              Object.entries(obj).forEach(([subKey, subVal]) => {
                                if (isEmptyOrNA(subVal)) return; // Skip N/A and empty values
                                const full = prefix ? `${prefix}.${subKey}` : `${k}.${subKey}`;
                                if (toDeleteSet.has(full)) return;
                                
                                if (typeof subVal === 'object' && !Array.isArray(subVal)) {
                                  // Recurse deeper for nested objects
                                  flattenNested(subVal, full);
                                } else if (Array.isArray(subVal)) {
                                  // For arrays, filter out N/A items and convert to readable strings
                                  const arrayStr = subVal
                                    .filter(item => !isEmptyOrNA(item))
                                    .map(item => {
                                      if (typeof item === 'object' && item !== null) {
                                        if (item.label) return item.label;
                                        if (item.name) return item.name;
                                        return JSON.stringify(item);
                                      }
                                      return String(item);
                                    }).join(', ');
                                  if (arrayStr) expanded.push([full, arrayStr]); // Only add if array had content
                                } else {
                                  // Primitive value
                                  expanded.push([full, subVal]);
                                }
                              });
                            };
                            flattenNested(v);
                          } else if (Array.isArray(v)) {
                            // Handle top-level arrays (like progression): expand objects within them
                            if (toDeleteSet.has(k)) return;
                            v.forEach((item, itemIdx) => {
                              if (isEmptyOrNA(item)) return; // Skip N/A and empty items
                              if (typeof item === 'object' && !Array.isArray(item)) {
                                // Expand object properties from array items (e.g., progression[0].description)
                                Object.entries(item).forEach(([subKey, subVal]) => {
                                  if (isEmptyOrNA(subVal)) return; // Skip N/A and empty values
                                  const full = `${k}[${itemIdx}].${subKey}`;
                                  if (toDeleteSet.has(full)) return;
                                  
                                  if (typeof subVal === 'object' && !Array.isArray(subVal)) {
                                    // For nested objects in array items, recursively flatten them
                                    const flattenNestedInArray = (obj, prefix = '') => {
                                      Object.entries(obj).forEach(([nestedKey, nestedVal]) => {
                                        if (isEmptyOrNA(nestedVal)) return;
                                        const nestedFull = prefix ? `${prefix}.${nestedKey}` : `${full}.${nestedKey}`;
                                        if (toDeleteSet.has(nestedFull)) return;
                                        
                                        if (typeof nestedVal === 'object' && !Array.isArray(nestedVal)) {
                                          flattenNestedInArray(nestedVal, nestedFull);
                                        } else if (Array.isArray(nestedVal)) {
                                          const arrayStr = nestedVal
                                            .filter(x => !isEmptyOrNA(x))
                                            .map(x => typeof x === 'object' && x !== null ? JSON.stringify(x) : String(x))
                                            .join(', ');
                                          if (arrayStr) expanded.push([nestedFull, arrayStr]);
                                        } else {
                                          expanded.push([nestedFull, nestedVal]);
                                        }
                                      });
                                    };
                                    flattenNestedInArray(subVal);
                                  } else if (Array.isArray(subVal)) {
                                    const arrayStr = subVal
                                      .filter(x => !isEmptyOrNA(x))
                                      .map(x => typeof x === 'object' && x !== null ? JSON.stringify(x) : String(x))
                                      .join(', ');
                                    if (arrayStr) expanded.push([full, arrayStr]);
                                  } else {
                                    expanded.push([full, subVal]);
                                  }
                                });
                              } else {
                                // Primitive array item
                                const full = `${k}[${itemIdx}]`;
                                if (!toDeleteSet.has(full)) {
                                  expanded.push([full, item]);
                                }
                              }
                            });
                          } else {
                            if (toDeleteSet.has(k)) return;
                            expanded.push([k, v]);
                          }
                        });

                        if (expanded.length === 0) return null;

                        const labelize = (s) => s
                          .replace(/\./g, ' - ')
                          .replace(/([A-Z])/g, ' $1')
                          .replace(/^./, (ch) => ch.toUpperCase());

                        // Group attributes by top-level key so nested objects like
                        // facialFeatures264 become a titled section with one line per sub-key.
                        const groupMap = new Map();
                        expanded.forEach(([fullKey, value]) => {
                          const dotIndex = fullKey.indexOf('.')
                          const groupKey = dotIndex === -1 ? '' : fullKey.slice(0, dotIndex);
                          const fieldKey = dotIndex === -1 ? fullKey : fullKey.slice(dotIndex + 1);
                          const arr = groupMap.get(groupKey) || [];
                          arr.push([fieldKey, value]);
                          groupMap.set(groupKey, arr);
                        });

                        const flatGroup = groupMap.get('') || [];
                        const nestedGroups = Array.from(groupMap.entries()).filter(([k]) => k !== '');

                        const uniqueFlat = (() => {
                          const seen = new Set();
                          const out = [];
                          flatGroup.forEach(([fieldKey, value]) => {
                            const norm = String(fieldKey).trim().toLowerCase();
                            if (!seen.has(norm)) {
                              seen.add(norm);
                              out.push([fieldKey, value]);
                            }
                          });
                          return out;
                        })();

                        const dedupeFields = (fields) => {
                          const seen = new Set();
                          const out = [];
                          fields.forEach(([fieldKey, value]) => {
                            const norm = String(fieldKey).trim().toLowerCase();
                            if (!seen.has(norm)) {
                              seen.add(norm);
                              out.push([fieldKey, value]);
                            }
                          });
                          return out;
                        };

                        return (
                          <div className="mt-3 text-sm flex-1 flex flex-col">
                            <div className={isExpanded ? '' : 'max-h-40 overflow-hidden'}>
                              <p className="font-medium text-gray-700">Attributes</p>
                              {/* Flat top-level attributes like age, ethnicity, build, etc. */}
                              {flatGroup.length > 0 && (
                                !isEditing ? (
                                  <ul className="mt-1 space-y-1 list-disc list-inside">
                                    {uniqueFlat.map(([fieldKey, value]) => {
                                      // Safety check: if value is still an object, skip it to avoid [object Object]
                                      if (typeof value === 'object' && value !== null) {
                                        return null;
                                      }
                                      return (
                                        <li key={String(fieldKey).trim().toLowerCase()} className="text-sm text-gray-600">
                                          <span className="text-gray-700">{labelize(fieldKey)}:</span>{' '}
                                          <span className="text-gray-900">{String(value)}</span>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                ) : (
                                  <div className="mt-1 space-y-2">
                                    {uniqueFlat.map(([fieldKey, value]) => (
                                      <div key={String(fieldKey).trim().toLowerCase()} className="flex items-center gap-2 pr-4 text-sm text-gray-600 w-full overflow-hidden">
                                        <span className="w-40 shrink-0 text-gray-500">{labelize(fieldKey)}</span>
                                        <input
                                          className="flex-1 min-w-0 border rounded px-2 py-1 text-gray-900"
                                          value={editing[uid]?.[fieldKey] ?? String(value)}
                                          onChange={(e) => onAttrChange(uid, fieldKey, e.target.value)}
                                        />
                                        <button
                                          type="button"
                                          aria-label={`Delete ${labelize(fieldKey)}`}
                                          title={`Delete ${labelize(fieldKey)}`}
                                          onClick={() => onDeleteAttr(uid, String(fieldKey))}
                                          className="flex-none inline-flex items-center justify-center w-7 h-7 rounded border text-red-600 border-red-200 bg-red-50 hover:bg-red-100"
                                        >
                                          <TrashIcon className="h-4 w-4" />
                                        </button>
                                      </div>
                                    ))}
                                    <div className="flex items-center gap-2 text-xs mt-2">
                                      <input
                                        className="w-40 border rounded px-2 py-1 text-gray-900"
                                        placeholder="new field"
                                        value={addCharField[uid] ?? ''}
                                        onChange={(e) => setAddCharField((p) => ({ ...p, [uid]: e.target.value }))}
                                      />
                                      <input
                                        className="flex-1 border rounded px-2 py-1 text-gray-900"
                                        placeholder="value"
                                        value={addCharValue[uid] ?? ''}
                                        onChange={(e) => setAddCharValue((p) => ({ ...p, [uid]: e.target.value }))}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => onAddAttr(uid)}
                                        className="inline-flex items-center justify-center w-6 h-6 rounded border text-indigo-600 border-indigo-200 bg-indigo-50 hover:bg-indigo-100"
                                        title="Add attribute"
                                      >
                                        <PlusIcon className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                  </div>
                                )
                              )}

                              {/* Nested object attributes grouped by top-level key, e.g. Facial Features264 */}
                              {nestedGroups.map(([groupKey, fields]) => (
                                <div key={groupKey} className="mt-3">
                                  <p className="font-medium text-gray-700">{labelize(groupKey)}</p>
                                  {!isEditing ? (
                                    <ul className="mt-1 space-y-1 list-disc list-inside">
                                      {dedupeFields(fields).map(([fieldKey, value]) => {
                                        // Safety check: if value is still an object, skip it to avoid [object Object]
                                        if (typeof value === 'object' && value !== null) {
                                          return null;
                                        }
                                        return (
                                          <li key={`${groupKey}.${String(fieldKey).trim().toLowerCase()}`} className="text-sm text-gray-600">
                                            <span className="text-gray-700">{labelize(fieldKey)}:</span>{' '}
                                            <span className="text-gray-900">{String(value)}</span>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  ) : (
                                    <div className="mt-1 space-y-2">
                                      {dedupeFields(fields).map(([fieldKey, value]) => (
                                        <div key={`${groupKey}.${String(fieldKey).trim().toLowerCase()}`} className="flex items-center gap-2 pr-4 text-sm text-gray-600 w-full overflow-hidden">
                                          <span className="w-40 shrink-0 text-gray-500">{labelize(fieldKey)}</span>
                                          <input
                                            className="flex-1 min-w-0 border rounded px-2 py-1 text-gray-900"
                                            value={editing[uid]?.[`${groupKey}.${fieldKey}`] ?? String(value)}
                                            onChange={(e) => onAttrChange(uid, `${groupKey}.${fieldKey}`, e.target.value)}
                                          />
                                          <button
                                            type="button"
                                            aria-label={`Delete ${labelize(groupKey)} ${labelize(fieldKey)}`}
                                            title={`Delete ${labelize(groupKey)} ${labelize(fieldKey)}`}
                                            onClick={() => onDeleteAttr(uid, `${groupKey}.${fieldKey}`)}
                                            className="flex-none inline-flex items-center justify-center w-7 h-7 rounded border text-red-600 border-red-200 bg-red-50 hover:bg-red-100"
                                          >
                                            <TrashIcon className="h-4 w-4" />
                                          </button>
                                        </div>
                                      ))}
                                      <div className="flex items-center gap-2 text-xs mt-2">
                                        <input
                                          className="w-40 border rounded px-2 py-1 text-gray-900"
                                          placeholder="new field"
                                          value={addGroupField[`${uid}::${groupKey}`] ?? ''}
                                          onChange={(e) => setAddGroupField((p) => ({ ...p, [`${uid}::${groupKey}`]: e.target.value }))}
                                        />
                                        <input
                                          className="flex-1 border rounded px-2 py-1 text-gray-900"
                                          placeholder="value"
                                          value={addGroupValue[`${uid}::${groupKey}`] ?? ''}
                                          onChange={(e) => setAddGroupValue((p) => ({ ...p, [`${uid}::${groupKey}`]: e.target.value }))}
                                        />
                                        <button
                                          type="button"
                                          onClick={() => onAddGroupAttr(uid, groupKey)}
                                          className="inline-flex items-center justify-center w-6 h-6 rounded border text-indigo-600 border-indigo-200 bg-indigo-50 hover:bg-indigo-100"
                                          title={`Add ${labelize(groupKey)} attribute`}
                                        >
                                          <PlusIcon className="h-3.5 w-3.5" />
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                            {(flatGroup.length > 0 || nestedGroups.length > 0) && (
                              <div className="mt-auto pt-3 flex flex-col items-center">
                                <div className="w-full border-t border-gray-200" />
                                <button
                                  type="button"
                                  onClick={toggleExpanded}
                                  className="mt-1 px-3 py-1 text-sm font-semibold text-indigo-600 hover:text-indigo-800 flex items-center justify-center"
                                >
                                  {isExpanded ? 'View less' : 'View more'}
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      } catch (_) {
                        return null;
                      }
                    })()}
                  </div>
                );
              })}
            </div>
          )
        ) : isPlacesTab ? (
          displayPlaces.length === 0 ? (
            placesRegenStatus === 'pending' ? (
              <div className="text-sm text-gray-600">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-5 h-5 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
                  <p>Generating places from your story... This may take a moment.</p>
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-1/2" />
                </div>
              </div>
            ) : placesRegenStatus === 'error' ? (
              <div className="text-sm text-red-600">
                <p className="mb-2 font-semibold">Places generation failed.</p>
                <p className="mb-3">Please try again or contact support if the issue persists.</p>
                <button
                  type="button"
                  onClick={handleRegeneratePlacesOnly}
                  disabled={regenerating}
                  className="inline-flex items-center px-3 py-1.5 rounded-md border border-red-300 text-red-700 hover:bg-red-50 text-xs font-medium disabled:opacity-50"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <div className="text-sm text-gray-600">
                <p className="mb-2">
                  Something went wrong and we couldn&apos;t load your places.
                </p>
                <button
                  type="button"
                  onClick={handleRegeneratePlacesOnly}
                  disabled={regenerating || regenStatus === 'pending'}
                  className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs font-medium disabled:opacity-50"
                >
                  {regenerating || regenStatus === 'pending' ? 'Regenerating...' : 'Generate again from Story'}
                </button>
              </div>
            )
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayPlaces.map((p) => {
                const uid = p._uid || generateElementUid(p);
                const isEditingP = editingPlaces[uid] != null;
                const viewP = isEditingP ? editingPlaces[uid] : p;
                return (
                  <div key={uid} className="rounded-lg border border-gray-200 shadow-sm p-4 bg-white flex flex-col h-full">
                    <div className="flex items-start justify-between mb-1">
                      <h3 className="font-semibold text-gray-900">{getName(viewP) || 'Unnamed'}</h3>
                      <button onClick={() => openModal('place', uid)} className="text-sm px-2 py-1 rounded border hover:bg-gray-50">Edit</button>
                    </div>
                    <div className="mb-2 w-full aspect-[3/2] bg-gray-100 rounded overflow-hidden flex items-center justify-center relative cursor-pointer" onClick={() => openModal('place', uid)}>
                      {pendingJobs.has(uid) ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm">
                          <div className="w-10 h-10 border-4 border-gray-300 border-t-indigo-600 rounded-full animate-spin" />
                        </div>
                      ) : null}
                      {viewP?.referenceImage ? (
                        <img
                          key={viewP.referenceImage}
                          src={viewP.referenceImage}
                          alt={getName(viewP) || 'Place image'}
                          className="w-full h-full object-contain"

                        />
                      ) : (
                        <div className="text-gray-400 text-sm">No image</div>
                      )}
                    </div>

                    {(() => {
                      try {
                        const base = viewP || {};
                        const entries = Object.entries(base).filter(([k, v]) => (
                          k !== 'name' && k !== 'Name' && k !== 'mainName' && k !== 'nickName' && k !== 'nickname' && k !== 'Alias' && k !== 'alias' && k !== 'TrueName' && k !== 'trueName' &&
                          k !== 'description' &&
                          k !== 'imageUrl' &&
                          k !== 'referenceImage' &&
                          k !== '_uid' &&
                          v !== undefined && v !== null && typeof v !== 'object'
                        ));
                        if (entries.length === 0) return null;
                        const labelize = (s) => s.replace(/([A-Z])/g, ' $1').replace(/^./, (ch) => ch.toUpperCase());
                        return (
                          <div className="mt-3 text-sm flex-1 flex flex-col">
                            <div className="max-h-40 overflow-hidden">
                              <p className="font-medium text-gray-700">Attributes</p>
                              {!isEditingP ? (
                                <dl className="mt-1 space-y-1">
                                  {entries.map(([k, v]) => (
                                    <div key={k} className="flex justify-between items-start gap-2">
                                      <dt className="text-gray-500">{labelize(k)}</dt>
                                      <dd className="text-gray-900 text-right break-words">{String(v)}</dd>
                                    </div>
                                  ))}
                                </dl>
                              ) : (
                                <div className="mt-1 space-y-2">
                                  {entries.map(([k]) => (
                                    <label key={k} className="flex items-center gap-2">
                                      <span className="w-28 shrink-0 text-gray-500">{labelize(k)}</span>
                                      <input className="flex-1 border rounded px-2 py-1 text-gray-900" value={editingPlaces[uid]?.[k] ?? ''} onChange={(e) => onPlaceAttrChange(uid, k, e.target.value)} />
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                            {entries.length > 0 && (
                              <div className="mt-auto pt-3 flex flex-col items-center">
                                <div className="w-full border-t border-gray-200" />
                                <button
                                  type="button"
                                  onClick={() => openModal('place', uid)}
                                  className="mt-1 px-3 py-1 text-sm font-semibold text-indigo-600 hover:text-indigo-800 flex items-center justify-center"
                                >
                                  View more
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      } catch (_) {
                        return null;
                      }
                    })()}
                  </div>
                );
              })}
            </div>
          )
        ) : isThingsTab ? (
          displayThings.length === 0 ? (
            thingsRegenStatus === 'pending' ? (
              <div className="text-sm text-gray-600">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-5 h-5 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
                  <p>Generating things from your story... This may take a moment.</p>
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-1/2" />
                </div>
              </div>
            ) : thingsRegenStatus === 'error' ? (
              <div className="text-sm text-red-600">
                <p className="mb-2 font-semibold">Things generation failed.</p>
                <p className="mb-3">Please try again or contact support if the issue persists.</p>
                <button
                  type="button"
                  onClick={handleRegenerateThingsOnly}
                  disabled={regenerating}
                  className="inline-flex items-center px-3 py-1.5 rounded-md border border-red-300 text-red-700 hover:bg-red-50 text-xs font-medium disabled:opacity-50"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <div className="text-sm text-gray-600">
                <p className="mb-2">
                  Something went wrong and we couldn&apos;t load your things.
                </p>
                <button
                  type="button"
                  onClick={handleRegenerateThingsOnly}
                  disabled={regenerating || regenStatus === 'pending'}
                  className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs font-medium disabled:opacity-50"
                >
                  {regenerating || regenStatus === 'pending' ? 'Regenerating...' : 'Generate again from Story'}
                </button>
              </div>
            )
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayThings.map((t) => {
                const uid = t._uid || generateElementUid(t);
                const isEditingT = editingThings[uid] != null;
                const viewT = isEditingT ? editingThings[uid] : t;
                return (
                  <div key={uid} className="rounded-lg border border-gray-200 shadow-sm p-4 bg-white flex flex-col h-full">
                    <div className="flex items-start justify-between mb-1">
                      <h3 className="font-semibold text-gray-900">{getName(viewT) || 'Unnamed'}</h3>
                      <button onClick={() => openModal('thing', uid)} className="text-sm px-2 py-1 rounded border hover:bg-gray-50">Edit</button>
                    </div>
                    <div className="mb-2 w-full aspect-[3/2] bg-gray-100 rounded overflow-hidden flex items-center justify-center relative cursor-pointer" onClick={() => openModal('thing', uid)}>
                      {pendingJobs.has(uid) ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm">
                          <div className="w-10 h-10 border-4 border-gray-300 border-t-indigo-600 rounded-full animate-spin" />
                        </div>
                      ) : null}
                      {viewT?.referenceImage ? (
                        <img
                          key={viewT.referenceImage}
                          src={viewT.referenceImage}
                          alt={getName(viewT) || 'Thing image'}
                          className="w-full h-full object-contain"

                        />
                      ) : (
                        <div className="text-gray-400 text-sm">No image</div>
                      )}
                    </div>

                    {(() => {
                      try {
                        const base = viewT || {};
                        const entries = Object.entries(base).filter(([k, v]) => (
                          k !== 'name' && k !== 'Name' && k !== 'mainName' && k !== 'nickName' && k !== 'nickname' && k !== 'Alias' && k !== 'alias' && k !== 'TrueName' && k !== 'trueName' &&
                          k !== 'description' &&
                          k !== 'imageUrl' &&
                          k !== '_uid' &&
                          v !== undefined && v !== null && typeof v !== 'object'
                        ));
                        if (entries.length === 0) return null;
                        const labelize = (s) => s.replace(/([A-Z])/g, ' $1').replace(/^./, (ch) => ch.toUpperCase());
                        return (
                          <div className="mt-3 text-sm flex-1 flex flex-col">
                            <div className="max-h-40 overflow-hidden">
                              <p className="font-medium text-gray-700">Attributes</p>
                              {!isEditingT ? (
                                <dl className="mt-1 grid grid-cols-1 gap-y-1">
                                  {entries.map(([k, v]) => (
                                    <div key={k} className="flex justify-between items-start gap-2">
                                      <dt className="text-gray-500">{labelize(k)}</dt>
                                      <dd className="text-gray-900 text-left break-words">{String(v)}</dd>
                                    </div>
                                  ))}
                                </dl>
                              ) : (
                                <div className="mt-1 grid grid-cols-1 gap-2">
                                  {entries.map(([k]) => (
                                    <label key={k} className="flex items-center gap-2">
                                      <span className="w-28 shrink-0 text-gray-500">{labelize(k)}</span>
                                      <input className="flex-1 border rounded px-2 py-1 text-gray-900" value={editingThings[uid]?.[k] ?? ''} onChange={(e) => onThingAttrChange(uid, k, e.target.value)} />
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                            {entries.length > 0 && (
                              <div className="mt-auto pt-3 flex flex-col items-center">
                                <div className="w-full border-t border-gray-200" />
                                <button
                                  type="button"
                                  onClick={() => openModal('thing', uid)}
                                  className="mt-1 px-3 py-1 text-sm font-semibold text-indigo-600 hover:text-indigo-800 flex items-center justify-center"
                                >
                                  View more
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      } catch (_) {
                        return null;
                      }
                    })()}
                  </div>
                );
              })}
            </div>
          )
        ) : (
          safeItems.length === 0 ? (
            <p className="text-sm text-gray-500">No {currentTab?.label?.toLowerCase()} yet.</p>
          ) : (
            <ul className="list-disc list-inside space-y-1">
              {safeItems.map((item, idx) => (
                <li key={idx} className="text-gray-800">{typeof item === 'string' ? item : (getName(item) || 'Unnamed')}</li>
              ))}
            </ul>
          )
        )}
      </section>

      {(() => {
        const currentBulkState = getBulkState(activeTab);
        return (
          <BulkImageGenerationOverlay
            isOpen={bulkDialogOpen}
            onClose={() => setBulkDialogOpen(false)}
            isGenerating={currentBulkState.generating}
            generatingMode={currentBulkState.generatingMode}
            overwriteElementsCount={overwriteImagesCount}
            missingElementsCount={missingImagesCount}
            selectedStyleValue={currentBulkState.selectedStyle}
            coverOrientation="landscape_4_3"
            interiorOrientation="portrait_4_3"
            onGenerateMissingImages={async () => {
              const style = currentBulkState.selectedStyle || undefined;
              await handleGenerateAllImages('missing', style);
              // Modal stays open with loading spinner on buttons
              // Minimized version shows in background with real-time progress
            }}
            onGenerateAllImages={async () => {
              const style = currentBulkState.selectedStyle || undefined;
              await handleGenerateAllImages('overwrite', style);
              // Modal stays open with loading spinner on buttons
              // Minimized version shows in background with real-time progress
            }}
            onStyleChange={(value) => setBulkState(activeTab, { selectedStyle: value })}
            onCoverOrientationChange={() => {}}
            onInteriorOrientationChange={() => {}}
            pendingJobsCount={pendingJobsCount}
            completedJobsCount={completedJobsCount}
            bulkTotal={currentBulkState.total}
            bulkSessionStartPendingCount={currentBulkState.startPendingCount}
          />
        );
      })()}

      {/* Minimized Generation Progress - Shows when there are pending jobs */}
      <MinimizedGenerationProgress
        isVisible={pendingJobsCount > 0}
        pendingJobsCount={pendingJobsCount}
        bulkTotal={getBulkState(activeTab).total}
        onMaximize={() => setBulkDialogOpen(true)}
      />

      </div>

      {singleDialog.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={closeSingleDialog}
          />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-5">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Generate image</h3>
            <p className="text-sm text-gray-600 mb-4">This will cost {GENERATION_COST} Credits.</p>

            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Visual Style</p>
                <select
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                  value={singleSelectedVisualStyle}
                  onChange={(e) => setSingleSelectedVisualStyle(e.target.value)}
                >
                  <option value="">Use book style{currentBook?.visualStyle?` (${typeof currentBook.visualStyle==='object'?currentBook.visualStyle?.value:currentBook.visualStyle})`:''}</option>
                  {visualStyles.map((opt) => (
                    <option key={opt.id} value={opt.value}>{opt.title}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  className="px-3 py-1.5 rounded border text-sm hover:bg-gray-50"
                  onClick={closeSingleDialog}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                  onClick={confirmSingleGenerate}
                >
                  Generate - {GENERATION_COST} Credits
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center"
          onClick={() => setLightboxUrl(null)}
        >
          <img
            src={lightboxUrl}
            alt="Preview"
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            aria-label="Close"
            className="absolute top-4 right-4 text-white/80 hover:text-white text-2xl"
            onClick={() => setLightboxUrl(null)}
          >
            ×
          </button>
        </div>
      )}

      <WorldObjectModal
        isOpen={modalState.isOpen}
        objectType={modalState.objectType}
        object={getModalObject()}
        onClose={closeModal}
        onSave={handleModalSave}
        onDelete={handleModalDelete}
        onGenerateImage={handleModalGenerateImage}
        generatingImageUid={modalState.uid && (pendingJobs.has(modalState.uid) || requestingImageUids.has(modalState.uid)) ? modalState.uid : null}
        GENERATION_COST={GENERATION_COST}
        currentBook={currentBook}
      />
    </div>
  );
}
